import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import CustomerSelect from '../common/CustomerSelect';
import quoteService from '../../services/quoteService';
import inventoryService from '../../services/inventoryService';
import storeTypeService from '../../services/storeTypeService';
import SkuImage from '../common/SkuImage';
import apiClient from '../../services/apiClient';
import { useStoreFilter, getDefaultStoreType } from '../../hooks/useStoreFilter';
import { useAuth } from '../../context/AuthContext';
import { isAdmin, isManager } from '../../utils/roleUtils';
import customerService from '../../services/customerService';
import supplierService from '../../services/supplierService';
import { ENDPOINTS } from '../../config';
import { useToast } from '../../hooks/useToast';
import { Toast } from '../common/Toast';
import { format, parseISO } from 'date-fns';
import { FaTrash, FaInfoCircle } from 'react-icons/fa';
import debounce from 'lodash/debounce';
import { 
  getCartonConstraints, 
  calculateM2Rounding, 
  formatCartonInfo,
  CartonConstraint,
  M2RoundingResult 
} from '../../utils/cartonRounding';
import ExitConfirmationDialog from '../common/ExitConfirmationDialog';
import ShippingConfirmationDialog from '../common/ShippingConfirmationDialog';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import invoicesNotesIcon from '../../assets/invoicesNotesIcon.png';

// Breadcrumb navigation with enhanced styling
const BreadcrumbNav = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0;
  font-size: 1.1rem;
  color: #6B7280;
  width: 100%;
  padding: 12px 20px;
  background-color: #F9FAFB;
  border-top: 1px solid #E5E7EB;
  border-left: 1px solid #E5E7EB;
  border-right: 1px solid #E5E7EB;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 0.5rem;
  color: #6B7280;
`;

const EditInvoiceTitle = styled.span`
  font-weight: 700;
  color: #111827;
  font-size: 1.25rem;
`;

const InvoiceText = styled.span`
  color: #6B7280;
  font-weight: 500;
`;

const BackButton = styled.a`
  display: flex;
  align-items: center;
  color: #042B41;
  text-decoration: none;
  font-weight: 500;
  margin-bottom: 1rem;
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 16px;
  }
`;

const FormContainer = styled.form`
  width: 100%;
  border: 1px solid #E5E7EB;
  border-radius: 0 0 4px 4px;
  padding: 1rem;
  background-color: white;
  margin-top: 0;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const FormColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FormBoxesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
  width: 100%;
`;

const FormBoxHeading = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.5rem;
  white-space: nowrap;
  
  &::after {
    content: attr(data-required);
    color: #ef4444;
  }
`;

const FormItemBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;

  &.notes-field {
    flex: 2;
  }

  &.quote-field {
    flex: 2;
  }

  &.date-field {
    flex: 1.2;
    min-width: 180px;
  }

  &.checkbox-field {
    flex: 1;
    display: flex;
    align-items: flex-start;
    margin-top: 0;
  }
`;

const FormInput = styled.input<{ disabled?: boolean }>`
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  background-color: ${props => props.disabled ? '#F9FAFB' : 'white'};
  color: ${props => props.disabled ? '#6B7280' : '#111827'};
  height: 36px;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#D1D5DB' : '#042B41'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 2px rgba(4, 43, 65, 0.1)'};
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

const FormSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  background-color: white;
  height: 36px;
  width: 100%;
  appearance: none;
  
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
`;

// Add SelectWrapper for custom dropdown icon
const SelectWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #333;
    pointer-events: none;
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  min-height: 36px;
  resize: vertical;
  background-color: white;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
`;

// Specifically for the Notes field
const NotesTextarea = styled(FormTextarea)`
  height: 36px;
  resize: none;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 25px;
  white-space: nowrap;
  justify-content: flex-end;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  margin: 0 0.375rem 0 0;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  line-height: 1;
  user-select: none;
  font-weight: 500;
`;

// Styled components for customer pickup checkbox
const CustomerPickupContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  white-space: nowrap;
  justify-content: flex-end;
`;

const CustomerPickupInput = styled.input`
  width: 16px;
  height: 16px;
  margin: 0 0.375rem 0 0;
  cursor: pointer;
`;

const CustomerPickupLabel = styled.label`
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  line-height: 1;
  user-select: none;
  font-weight: 500;
`;

const DeliverToAddressInput = styled(FormInput)<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? '#F9FAFB' : 'white'};
  color: ${props => props.disabled ? '#6B7280' : '#111827'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
`;

const StoreType = styled.div`
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  background-color: #F9FAFB;
  height: 36px;
  display: flex;
  align-items: center;
  width: 100%;
`;

const BottomRowContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;
`;

const DateAndCheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 4px;
  /* Add smooth scrolling for better UX */
  -webkit-overflow-scrolling: touch;
  
  /* Styling the scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1; 
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1; 
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8; 
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 1200px; /* Force minimum width to trigger scrollbar on small screens */
  border-collapse: collapse;
  margin-top: 0;
  border: 1px solid #FFFFFF;
  position: relative;
  z-index: 1;
`;

const TableHeader = styled.thead`
  background-color: #042B41;
  color: white;
  
  th {
    padding: 0.75rem;
    text-align: left;
    font-weight: 500;
    font-size: 0.875rem;
    border: 0.5px solid white;
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #E5E7EB;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  td {
    padding: 0.75rem;
    font-size: 0.875rem;
    border: 1px solid #E5E7EB;
    position: relative;
    overflow: visible;
    vertical-align: top;
    
    &.wrap {
      white-space: normal;
      word-break: break-word;
      overflow: visible;
    }
  }
`;

const TotalSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 2rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 0.75rem;
  
  &:last-child {
    margin-bottom: 0;
    margin-top: 0.5rem;
  }
`;

// Add tooltip styling
const Tooltip = styled.div`
  position: absolute;
  top: -60px;
  right: 0;
  width: 250px;
  padding: 10px;
  background-color: #333;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  z-index: 100;
  visibility: hidden;
  opacity: 0;
  transition: visibility 0s, opacity 0.3s ease;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    right: 10px;
    border-width: 10px 10px 0;
    border-style: solid;
    border-color: #333 transparent transparent;
  }
`;

const InfoIconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  margin-right: 8px;
  
  &:hover ${Tooltip} {
    visibility: visible;
    opacity: 1;
  }
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #3B82F6;
  color: white;
  font-size: 12px;
  font-weight: bold;
  cursor: help;
`;

const TotalLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  width: 180px;
  text-align: right;
  margin-right: 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const TotalValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  width: 80px;
  text-align: right;
`;

const TotalInput = styled.input`
  font-size: 0.875rem;
  font-weight: 500;
  width: 120px;
  text-align: right;
  padding: 4px 8px;
  border: 1px solid #D1D5DB;
  border-radius: 4px;
  
  &::placeholder {
    color: #9CA3AF;
    font-weight: normal;
  }
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 1px #3B82F6;
  }
  
  &:disabled {
    background-color: #F9FAFB;
    color: #6B7280;
    cursor: not-allowed;
  }
`;

// Add currency symbol component
const CurrencySymbol = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  margin-right: 4px;
`;

const Divider = styled.hr`
  width: 280px;
  margin: 0.5rem 0;
  border: none;
  border-top: 1px solid #E5E7EB;
  align-self: flex-end;
`;

const GrandTotalLabel = styled(TotalLabel)`
  font-weight: 600;
`;

const GrandTotalValue = styled(TotalValue)`
  font-weight: 600;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  width: 640px;
  margin-left: auto;
  margin-right: auto;
`;

const ActionButton = styled.button`
  padding: 0.75rem 2rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  width: 200px;
  height: 48px;
  transition: background-color 0.2s;
`;

const CancelButton = styled(ActionButton)`
  border: 1px solid #D1D5DB;
  background-color: white;
  color: #111827;
  
  &:hover {
    background-color: #F9FAFB;
  }
`;

const SubmitButton = styled(ActionButton)`
  border: none;
  background-color: #042B41;
  color: white;
  
  &:hover {
    background-color: #031F30;
  }
`;

const SaveAsDraftButton = styled(CancelButton)``;

const QuantityInput = styled(FormInput)<{ isDisabled?: boolean }>`
  width: 60px;
  text-align: center;
  background-color: ${props => props.isDisabled ? '#f3f4f6' : 'white'};
  color: ${props => props.isDisabled ? '#9ca3af' : '#111827'};
  cursor: ${props => props.isDisabled ? 'not-allowed' : 'text'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.isDisabled ? '#D1D5DB' : '#042B41'};
    box-shadow: ${props => props.isDisabled ? 'none' : '0 0 0 2px rgba(4, 43, 65, 0.1)'};
  }
`;

const PriceInput = styled(FormInput)`
  width: 100%;
  max-width: 110px;
  text-align: right;
  box-sizing: border-box;
`;

const AddItemButton = styled.button<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? '#D1D5DB' : '#042B41'};
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 18px;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    background-color: ${props => props.disabled ? '#D1D5DB' : '#031F30'};
  }
`;

const RemoveItemButton = styled.button`
  background-color: white;
  color: #EF4444;
  border: 1px solid #EF4444;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  
  &:hover {
    background-color: #FEF2F2;
  }
`;

const NotesIcon = styled.span<{ hasNotes?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    filter: ${props => props.hasNotes ? 'invert(15%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(95%) contrast(112%)' : 'none'};
  }
`;

const NotesModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const NotesModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const NotesModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`;

const NotesModalTextarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border: 1px solid #D1D5DB;
  border-radius: 4px;
  font-size: 0.875rem;
  resize: vertical;
  background-color: #F9FAFB;
  color: #111827;
  cursor: default;
  
  &:focus {
    outline: none;
    border-color: #042B41;
  }
`;

const NotesButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const NotesButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background-color: #042B41;
  color: white;

  &:hover {
    background-color: #031F30;
  }
`;

const CloseNotesButton = styled(NotesButton)`
  background-color: white;
  color: #111827;
  border: 1px solid #D1D5DB;

  &:hover {
    background-color: #F9FAFB;
  }
`;

// Add a wrapper styled component for CustomerSelect
const CustomerSelectWrapper = styled.div`
  input {
    height: 36px !important;
    font-size: 0.875rem;
    padding: 0.5rem;
    border-radius: 0.25rem;
  }
`;

// Create a DateInput component with the right styling
const DateInput = styled(FormInput)`
  height: 36px;
  padding: 0.375rem 0.5rem;
  width: 100%;
  min-width: 130px;
`;

// Date input wrapper without calendar icon
const DateInputWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 180px;
`;

// Styled date input that shows the date picker
const StyledDateInput = styled(FormInput)`
  height: 36px;
  padding: 0.375rem 0.5rem;
  width: 100%;
  min-width: 180px;
  cursor: pointer;
  font-size: 0.875rem;
  
  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    cursor: pointer;
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
  
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    display: none;
  }
  
  /* Additional styling for better calendar positioning */
  &::-webkit-datetime-edit {
    width: 100%;
    padding: 0;
  }
  
  &::-webkit-datetime-edit-fields-wrapper {
    width: 100%;
  }
`;

// Create a QuoteSelect component for consistent styling - now searchable
const QuoteSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const QuoteSearchInput = styled(FormInput)`
  height: 36px;
  padding: 0.375rem 0.5rem;
  width: 100%;
`;

const QuoteDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #D1D5DB;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const QuoteOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

// SKU Select component - now searchable
const SKUSelectWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 1;
`;

const SKUSearchInput = styled.textarea`
  padding: 0.625rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  letter-spacing: 0.5px;
  font-family: monospace;
  min-height: 48px;
  resize: vertical;
  line-height: 1.25rem;
  overflow: auto;
  background-color: white;
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
`;

const SKUDropdown = styled.div<{ isOpen: boolean; top: number; left: number; width: number }>`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  width: ${props => props.width}px;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  max-height: 300px;
  overflow-y: auto;
  z-index: 99999;
  display: ${props => props.isOpen ? 'block' : 'none'};
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  min-width: 200px;
`;

const SKUOption = styled.div`
  padding: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  background-color: white;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: #F3F4F6;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SKUOptionContent = styled.div`
  flex: 1;
  pointer-events: none; /* Prevent event conflicts with image clicks */
`;

const SelectedSKUContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;



// Helper text for PO Number
const HelperText = styled.div`
  font-size: 0.75rem;
  color: #6B7280;
  margin-top: 0.25rem;
  font-style: italic;
`;

// Add styled components for shipping modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 700px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #E5E7EB;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6B7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  color: #374151;
  line-height: 1.6;
  
  h3 {
    color: #111827;
    font-weight: 600;
    margin: 16px 0 8px 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  p {
    margin: 8px 0;
  }
  
  ul {
    margin: 8px 0;
    padding-left: 20px;
  }
  
  li {
    margin: 4px 0;
  }
`;

// Update the InfoIcon to be clickable
const ClickableInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #3B82F6;
  color: white;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-right: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #2563EB;
  }
`;

// Small info icon for carton info in total column
const SmallInfoIcon = styled.span<{ showTooltip: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #F59E0B;
  color: white;
  font-size: 10px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 4px;
  transition: background-color 0.2s;
  position: relative;
  
  &:hover {
    background-color: #D97706;
  }
`;

// Carton info tooltip (simple tooltip, not modal)
const CartonInfoTooltip = styled.div<{ show: boolean }>`
  position: absolute;
  top: -45px;
  right: -150px;
  width: 280px;
  background-color: #FEF3C7;
  border: 1px solid #F59E0B;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 11px;
  color: #D97706;
  line-height: 1.3;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;
`;

interface InvoiceItemQuantity {
  units: number | null;
  boxes: number | null;
  pieces: number | null;
  m2: number | null;
  blm: number | null;
  total: number;
}

interface InvoiceItem {
  id: string;
  sku: string;
  description: string;
  quantity: InvoiceItemQuantity;
  unitPrice: number;
  totalPrice: number;
  deductFromStock: boolean;
  cartonConstraint?: CartonConstraint;
  m2RoundingResult?: M2RoundingResult;
  boxesFromM2Rounding?: boolean; // Track if boxes were set programmatically
  notes?: string;
  [key: string]: any; // Allow string indexing
}

interface InvoiceTotals {
  orderTotal: number;
  surcharge: number;
  gst: number;
  discount: number;
  shipping: number;
  grandTotal: number;
}

// Update the Quote interface to include both possible structures
interface Quote {
  id: string;
  quote_no: string;
  quote_items?: any[];
  items?: any[];
  deliver_to_address?: string;
  customer_id?: string;
  customer_name?: string;
  store_type?: string; // Add store_type field from API response
}

// Define interface for the new structure of quote items from API
interface QuoteItemAPI {
  sku_code?: string;
  sku?: string;
  description?: string;
  quantity_units?: number;
  quantity_boxes?: number;
  quantity_pieces?: number;
  quantity_m2?: number;
  quantity_blm?: number;
  units?: number;
  boxes?: number;
  pieces?: number;
  m2?: number;
  blm?: number;
  unit_price?: number;
  totalPrice?: number;
  total_price?: number;
}

interface InvoiceData {
  id?: string; // Add optional id field
  storeType: string;
  saleType: string;
  modeOfPayment: string;
  poNumber: string;
  invoiceTo: string;
  deliverToAddress: string;
  invoiceNo: string;
  notes: string;
  linkedQuote: string;
  date: string;
  dontSendPo: boolean;
  status?: string; // Add status field to track draft/saved status
  items: InvoiceItem[];
  totals: InvoiceTotals;
}

interface EditInvoiceProps {
  initialData?: InvoiceData;
  onSubmit: (data: InvoiceData) => void;
  onSaveAsDraft: (data: InvoiceData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  onHasChanges?: (hasChanges: boolean) => void;
}

// Custom styled section for the Create Invoice header - only keep one definition
const CreateInvoiceHeader = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 1rem 0;
`;

// Create a new Button component to replace the removed buttons
const Button = styled.button<{ primary?: boolean; secondary?: boolean }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${({ primary }) => primary && `
    background-color: #042B41;
    color: white;
    border: 1px solid #042B41;
    
    &:hover:not(:disabled) {
      background-color: #033957;
    }
  `}
  
  ${({ secondary }) => secondary && `
    background-color: white;
    color: #042B41;
    border: 1px solid #D1D5DB;
    
    &:hover:not(:disabled) {
      background-color: #F9FAFB;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Add interface for StoreType
interface StoreType {
  id: number;
  name: string;
  description?: string;
}

// Define ApiError interface for error type safety
interface ApiError {
  response?: {
    status: number;
    data: {
      detail?: string | any[];
      message?: string;
      [key: string]: any;
    };
    headers?: any;
  };
  message?: string;
  code?: string;
}

const EditInvoiceForm: React.FC<EditInvoiceProps> = ({
  initialData,
  onSubmit,
  onSaveAsDraft,
  onCancel,
  isLoading: parentIsLoading = false,
  onHasChanges
}) => {
  const navigate = useNavigate();
  const storeFilter = useStoreFilter();
  const { user } = useAuth();
  const toast = useToast();
  const canEditPrice = isAdmin(user) || isManager(user);
  const { setHasUnsavedChanges } = useUnsavedChanges();

  // Set up initial form data
  const [formData, setFormData] = useState<InvoiceData>(() => {
    if (initialData) {
      return { ...initialData };
    } else {
      // Initialize with one empty item (similar to Create New Quote)
      const newId = Math.random().toString(36).substring(2, 11);
      const initialItem: InvoiceItem = {
        id: newId,
        sku: '',
        description: '',
        quantity: {
          units: null,
          boxes: null,
          pieces: null,
          m2: null,
          blm: null,
          total: 0
        },
        unitPrice: 0,
        totalPrice: 0,
        deductFromStock: false
      };

      return {
        storeType: getDefaultStoreType(storeFilter)?.name || '',
        saleType: '',
        modeOfPayment: '',
        poNumber: '',
        invoiceTo: '',
        deliverToAddress: '',
        invoiceNo: '',
        notes: '',
        linkedQuote: '',
        date: new Date().toISOString().split('T')[0],
        dontSendPo: false,
        status: 'saved', // Default status for new invoices
        items: [initialItem],
        totals: {
          orderTotal: 0,
          surcharge: 0,
          gst: 0,
          discount: 0,
          shipping: 0,
          grandTotal: 0
        }
      };
    }
  });

  // Initialize SKU search terms from initial data if available
  const [skuSearchTerms, setSKUSearchTerms] = useState<{ [key: number]: string }>(() => {
    const initialTerms: { [key: number]: string } = {};
    if (initialData?.items) {
      initialData.items.forEach((item, index) => {
        initialTerms[index] = item.sku || '';
      });
    }
    return initialTerms;
  });

  // Local loading state for form operations
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState<boolean>(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [individualSkuData, setIndividualSkuData] = useState<{ [sku: string]: any }>({});
  const [storeTypes, setStoreTypes] = useState<StoreType[]>([]);
  const [isLoadingStoreTypes, setIsLoadingStoreTypes] = useState(false);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [lastInventoryRefresh, setLastInventoryRefresh] = useState<number>(Date.now());

  // State for searchable dropdowns
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('');
  const [isQuoteDropdownOpen, setIsQuoteDropdownOpen] = useState(false);
  const [openSKUDropdowns, setOpenSKUDropdowns] = useState<{ [key: number]: boolean }>({});
  
  // Add state for SKU dropdown positioning
  const [skuDropdownPositions, setSKUDropdownPositions] = useState<{ [key: number]: { top: number; left: number; width: number } }>({});

  // Ref for quote dropdown to handle outside clicks
  const quoteDropdownRef = useRef<HTMLDivElement>(null);
  
  // Add refs for SKU input elements
  const skuInputRefs = useRef<{ [key: number]: HTMLTextAreaElement | null }>({});

  // Add state to store customer details with price list info
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  // Add state to track if we should filter quotes by customer
  const [filterQuotesByCustomer, setFilterQuotesByCustomer] = useState<boolean>(false);

  // Add state for shipping details modal
  const [isShippingModalOpen, setIsShippingModalOpen] = useState(false);
  
  // Add state for Notes modal
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentNotesItemId, setCurrentNotesItemId] = useState<string | null>(null);
  const [currentNotesValue, setCurrentNotesValue] = useState<string>('');

  // Add state for carton info tooltip
  const [cartonInfoTooltip, setCartonInfoTooltip] = useState<{
    visible: boolean;
    itemIndex: number | null;
  }>({ visible: false, itemIndex: null });

  // Add state for raw shipping input to handle user typing
  const [shippingInputValue, setShippingInputValue] = useState<string>('');

  // Add state for validation errors
  const [stockValidationErrors, setStockValidationErrors] = useState<string[]>([]);
  const [requiredFieldErrors, setRequiredFieldErrors] = useState<string[]>([]);
  const [itemValidationErrors, setItemValidationErrors] = useState<string[]>([]);
  
  // Add state to track save button disabled state (for forcing re-renders)
  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(true);

  // Add state to track initial data for change detection
  const [initialFormData, setInitialFormData] = useState<InvoiceData | null>(null);

  // Add customer pickup state
  const [customerPickup, setCustomerPickup] = useState(false);

  // Add state for exit confirmation dialog
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  // Add state for shipping confirmation dialog
  const [showShippingConfirmation, setShowShippingConfirmation] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<(() => void) | null>(null);
  
  // Use a ref to track if we've set the store type to avoid unnecessary updates
  const storeTypeSetRef = useRef<boolean>(false);

  // Add state for carton constraints and M2 rounding
  const [cartonConstraints, setCartonConstraints] = useState<{ [sku: string]: CartonConstraint }>({});

  // Function to update the baseline after significant changes like quote loading
  const updateBaseline = useCallback(() => {
    setInitialFormData(JSON.parse(JSON.stringify(formData)));
  }, [formData]);

  // Notify parent component and context when form has changes
  useEffect(() => {
    const hasChanges = hasFormDataChanged();
    if (onHasChanges) {
      onHasChanges(hasChanges);
    }
    setHasUnsavedChanges(hasChanges);
  }, [formData, initialFormData, onHasChanges, setHasUnsavedChanges]);

  // Function to detect if form data has changed from initial data
  const hasFormDataChanged = (): boolean => {
    if (!initialFormData) {
      // If no initial data captured yet, consider it unchanged
      // This prevents enabling save button before baseline is established
      return false;
    }

    // Deep comparison of form data vs initial data
    const current = formData;
    const initial = initialFormData;

    // Compare basic fields
    if (
      current.storeType !== initial.storeType ||
      current.saleType !== initial.saleType ||
      current.modeOfPayment !== initial.modeOfPayment ||
      current.poNumber !== initial.poNumber ||
      current.invoiceTo !== initial.invoiceTo ||
      current.deliverToAddress !== initial.deliverToAddress ||
      current.notes !== initial.notes ||
      current.linkedQuote !== initial.linkedQuote ||
      current.date !== initial.date ||
      current.dontSendPo !== initial.dontSendPo
    ) {
      return true;
    }

    // Compare totals (excluding calculated fields that may change due to rounding)
    if (
      Math.abs(current.totals.discount - (initial.totals.discount || 0)) > 0.01 ||
      Math.abs(current.totals.shipping - (initial.totals.shipping || 0)) > 0.01
    ) {
      return true;
    }

    // Compare items array
    if (current.items.length !== initial.items.length) {
      return true;
    }

    for (let i = 0; i < current.items.length; i++) {
      const currentItem = current.items[i];
      const initialItem = initial.items[i];

      if (
        currentItem.sku !== initialItem.sku ||
        currentItem.description !== initialItem.description ||
        Math.abs(currentItem.unitPrice - initialItem.unitPrice) > 0.01 ||
        currentItem.deductFromStock !== initialItem.deductFromStock ||
        currentItem.quantity.units !== initialItem.quantity.units ||
        currentItem.quantity.boxes !== initialItem.quantity.boxes ||
        currentItem.quantity.pieces !== initialItem.quantity.pieces ||
        currentItem.quantity.m2 !== initialItem.quantity.m2 ||
        currentItem.quantity.blm !== initialItem.quantity.blm
      ) {
        return true;
      }
    }

    return false;
  };

  // Function to fetch individual SKU data
  const fetchIndividualSkuData = async (sku: string) => {
    if (!sku || individualSkuData[sku] !== undefined) {
      return; // Already fetched or empty SKU
    }

    try {
      console.log(`Pre-fetching inventory data for SKU: ${sku}`);
      const inventory = await inventoryService.getInventoryBySku(sku);
      if (inventory) {
        setIndividualSkuData(prev => ({
          ...prev,
          [sku]: inventory
        }));
        console.log(`SKU ${sku}: Pre-fetched inventory data with available_stock: ${inventory.available_stock}`);
      }
    } catch (error) {
      console.error(`Error pre-fetching inventory data for SKU ${sku}:`, error);
      // Store null to indicate failed fetch
      setIndividualSkuData(prev => ({
        ...prev,
        [sku]: null
      }));
    }
  };

  // Function to get inventory item data from both main inventory and individual SKU data
  const getInventoryItemData = (sku: string) => {
    // First check individual SKU data (most up-to-date)
    if (individualSkuData[sku]) {
      return individualSkuData[sku];
    }
    
    // Then check main inventory items
    let inventoryItem = inventoryItems.find(inv => inv.sku === sku);
    if (!inventoryItem) {
      inventoryItem = inventoryItems.find(inv => inv.sku_code === sku);
    }
    
    return inventoryItem;
  };

  // Function to automatically set deduct from stock based on availability
  const updateDeductFromStockBasedOnAvailability = (index: number, sku: string, totalQuantity: number) => {
    const inventoryItem = getInventoryItemData(sku);
    if (inventoryItem && totalQuantity > 0) {
      const availableStock = inventoryItem.available_stock || 0;
      const shouldDeduct = totalQuantity <= availableStock;
      
      // Update the item's deductFromStock based on availability
      setFormData(prev => {
        const newItems = [...prev.items];
        if (newItems[index]) {
          newItems[index].deductFromStock = shouldDeduct;
          console.log(`SKU ${sku}: Auto-setting deductFromStock to ${shouldDeduct} (available: ${availableStock}, required: ${totalQuantity})`);
        }
        return {
          ...prev,
          items: newItems
        };
      });
    }
  };

  // Function to validate stock availability for items with deductFromStock = true
  const validateStockAvailability = (): string[] => {
    const errors: string[] = [];
    
    formData.items.forEach((item) => {
      if (item.deductFromStock && item.sku) {
        const inventoryItem = getInventoryItemData(item.sku);
        
        if (inventoryItem) {
          const availableStock = inventoryItem.available_stock || 0;
          const requiredQuantity = item.quantity?.total || 0;
          
          if (requiredQuantity > availableStock) {
            errors.push(`${item.sku}: Required ${requiredQuantity}, Available ${availableStock}`);
          }
        } else {
          // Add error for missing inventory when deduction is required
          errors.push(`${item.sku}: Item not found in inventory`);
        }
      }
    });
    
    return errors;
  };

  // Function to validate required fields
  const validateRequiredFields = (): string[] => {
    const errors: string[] = [];
    
    // Check for required fields
    const requiredFields = [
      { field: 'storeType', label: 'Store Type' },
      { field: 'saleType', label: 'Sale Type' },
      { field: 'modeOfPayment', label: 'Mode of Payment' },
      { field: 'invoiceTo', label: 'Invoice To' },
      { field: 'deliverToAddress', label: 'Deliver to Address' },
      { field: 'date', label: 'Date' }
    ];

    // PO Number is no longer required for any sale type

    for (const { field, label } of requiredFields) {
      if (!formData[field as keyof InvoiceData] || formData[field as keyof InvoiceData] === '') {
        // Special handling for store type when user is store-restricted
        if (field === 'storeType' && storeFilter.isStoreRestricted && storeFilter.userStoreType) {
          // If user is store-restricted but store type is not set, set it automatically
          console.log('Auto-setting store type for restricted user:', storeFilter.userStoreType);
          setFormData(prev => ({
            ...prev,
            storeType: storeFilter.userStoreType || ''
          }));
        } else {
        errors.push(`${label} is required`);
        }
      }
    }

    return errors;
  };

  // Function to validate items
  const validateItems = (): string[] => {
    const errors: string[] = [];
    
    // Check if there are any items
    if (!formData.items || formData.items.length === 0) {
      errors.push('At least one item is required');
      return errors;
    }

    // Check if there are any valid items
    const validItems = formData.items.filter(item => {
      const hasQuantity =
        (item.quantity.units && item.quantity.units > 0) ||
        (item.quantity.boxes && item.quantity.boxes > 0) ||
        (item.quantity.pieces && item.quantity.pieces > 0) ||
        (item.quantity.m2 && item.quantity.m2 > 0);

      return item.sku && item.description && hasQuantity && item.unitPrice > 0;
    });

    if (validItems.length === 0) {
      errors.push('At least one valid item with SKU, description, quantity, and price is required');
    }

    return errors;
  };

  // Function to check if save button should be disabled
  const isSaveDisabled = (): boolean => {
    // Always disable if submitting or parent loading
    if (isSubmitting || parentIsLoading) {
      return true;
    }
    
    // Always disable if inventory is still loading and we have items that need stock validation
    const hasItemsNeedingStockValidation = formData.items.some(item => item.deductFromStock && item.sku);
    if (isLoadingInventory && hasItemsNeedingStockValidation) {
      return true;
    }
    
    const stockErrors = validateStockAvailability();
    const requiredFieldErrors = validateRequiredFields();
    const itemErrors = validateItems();
    
    return stockErrors.length > 0 || 
           requiredFieldErrors.length > 0 || 
           itemErrors.length > 0;
  };

  // Update all validation errors when form data changes
  useEffect(() => {
    const stockErrors = validateStockAvailability();
    const fieldErrors = validateRequiredFields();
    const itemErrors = validateItems();
    const hasChanges = hasFormDataChanged();
    
    setStockValidationErrors(stockErrors);
    setRequiredFieldErrors(fieldErrors);
    setItemValidationErrors(itemErrors);
    
    // Update save button disabled state
    // For draft invoices, allow saving even without changes (to convert draft to saved)
    // For saved invoices, require changes to be made before allowing save
    const isDraftInvoice = formData.status === 'draft';
    const hasValidationErrors = stockErrors.length > 0 || fieldErrors.length > 0 || itemErrors.length > 0;
    
    const shouldDisable = isSubmitting || 
                         parentIsLoading || 
                         (isLoadingInventory && formData.items.some(item => item.deductFromStock && item.sku)) ||
                         hasValidationErrors ||
                         (!isDraftInvoice && !hasChanges); // Only require changes for saved invoices
    
    setSaveButtonDisabled(shouldDisable);
  }, [formData, inventoryItems, individualSkuData, isLoadingInventory, isSubmitting, parentIsLoading, initialFormData]);

  // Debug function to help identify pricing issues
  const debugPricingIssue = (sku: string, customerName: string) => {
    console.group(`DEBUGGING PRICING ISSUE FOR ${customerName} - SKU: ${sku}`);
    
    console.log('1. Selected Customer Data:', selectedCustomer);
    console.log('2. Customer Price List ID:', selectedCustomer?.price_list_id);
    console.log('3. Customer Price List Name:', selectedCustomer?.price_list?.name);
    
    // Check if this is Alex Group
    if (customerName.toLowerCase().includes('alex') && customerName.toLowerCase().includes('group')) {
      console.log('This is Alex Group customer - Expected price list: "Regional Trade AUD Incl" (ID: 2)');
      console.log('Expected price for BS35-S: $1343.04');
      
      if (selectedCustomer?.price_list_id !== 2) {
        console.error('PROBLEM: Alex Group does not have price_list_id = 2');
        console.error('Current price_list_id:', selectedCustomer?.price_list_id);
        console.error('This explains why they are not getting the correct price');
      } else {
        console.log('Alex Group has correct price_list_id = 2');
      }
    }
    
    console.groupEnd();
  };

  // Enhanced pricing function with comprehensive debugging for Alex Group issue
  const debugAlexGroupPricing = (sku: string, customerPriceListId: number | null | undefined, supplierResponse: any) => {
    console.group(`ALEX GROUP PRICING DEBUG - SKU: ${sku}`);
    
    console.log('Input Parameters:');
    console.log('- SKU:', sku);
    console.log('- Customer Price List ID:', customerPriceListId);
    console.log('- Selected Customer:', selectedCustomer?.company_name || selectedCustomer?.name);
    
    console.log('Supplier Response:');
    console.log('- Success:', supplierResponse?.success);
    console.log('- Available Price List:', supplierResponse?.price_list);
    
    if (customerPriceListId === 2 && sku === 'BS35-S') {
      console.log('ALEX GROUP SPECIFIC CHECK:');
      console.log('- Expected Price List ID: 2 (Regional Trade AUD Incl)');
      console.log('- Expected Price: $1343.04');
      
      if (supplierResponse?.price_list?.['2']) {
        const actualPrice = supplierResponse.price_list['2'];
        console.log(`- Actual Price from API: $${actualPrice}`);
        
        if (Math.abs(actualPrice - 1343.04) < 0.01) {
          console.log('CORRECT: Alex Group will get the right price!');
        } else {
          console.error(`WRONG: Expected $1343.04 but got $${actualPrice}`);
        }
      } else {
        console.error('CRITICAL: No price found for price list ID "2"');
        console.error('Available keys:', Object.keys(supplierResponse?.price_list || {}));
      }
    }
    
    console.groupEnd();
  };

  // Function to get price for SKU based on customer's price list
  const getPriceForCustomerPriceList = (inventoryItem: any, customerPriceListId?: number | null): number => {
    // If no customer or no price list, fall back to trade_price
    if (!customerPriceListId || !inventoryItem) {
      console.warn('No customer price list ID provided, falling back to trade_price');
      return inventoryItem?.trade_price || 0;
    }

    console.log(`Getting price for customer price list ID: ${customerPriceListId}`, { inventoryItem });

    // Check if this is supplier data with PriceList mapping (PRIORITY)
    if (inventoryItem.PriceList && typeof inventoryItem.PriceList === 'object') {
      const priceListKey = customerPriceListId.toString();
      const priceFromPriceList = inventoryItem.PriceList[priceListKey];
      
      console.log(`Supplier PriceList lookup for customer price list ID ${customerPriceListId}:`, {
        priceListKey,
        priceFromPriceList,
        availablePrices: inventoryItem.PriceList
      });
      
      if (priceFromPriceList && priceFromPriceList > 0) {
        console.log(`Using supplier price: $${priceFromPriceList} for price list ID ${customerPriceListId}`);
        return priceFromPriceList;
      }
    }
    
    // Check direct price_list field (could be in response format)
    if (inventoryItem.price_list && typeof inventoryItem.price_list === 'object') {
      const priceListKey = customerPriceListId.toString();
      const priceFromPriceList = inventoryItem.price_list[priceListKey];
      
      console.log(`Direct price_list lookup for customer price list ID ${customerPriceListId}:`, {
        priceListKey,
        priceFromPriceList,
        availablePrices: inventoryItem.price_list
      });
      
      if (priceFromPriceList && priceFromPriceList > 0) {
        console.log(`Using direct price_list price: $${priceFromPriceList} for price list ID ${customerPriceListId}`);
        return priceFromPriceList;
      }
    }

    // For inventory items, we need to map the price list ID to the appropriate price field
    // Based on the actual database schema and supplier pricing logic
    // CORRECTED MAPPING based on run_custom_migration.py:
    console.log(`📋 Falling back to inventory pricing for price list ID ${customerPriceListId}`);
    switch (customerPriceListId) {
      case 1: // Regional Retail AUD Incl
        return inventoryItem.regional_retail_aud_incl || inventoryItem.retail_price || 0;
      case 2: // Regional Trade AUD Incl  
        return inventoryItem.regional_trade_aud_incl || inventoryItem.trade_price || 0;
      case 3: // Metro Retail AUD Incl
        return inventoryItem.metro_retail_aud_incl || inventoryItem.retail_price || 0;
      case 4: // Metro Trade AUD Incl
        return inventoryItem.metro_trade_aud_incl || inventoryItem.trade_price || 0;
      case 5: // Regional Retail VIP AUD Incl
        return inventoryItem.regional_retail_vip_aud_incl || inventoryItem.retail_price || 0;
      case 6: // Regional Trade VIP1 AUD Incl
        return inventoryItem.regional_trade_vip1_aud_incl || inventoryItem.trade_price || 0;
      case 7: // Regional Trade VIP2 AUD Incl
        return inventoryItem.regional_trade_vip2_aud_incl || inventoryItem.trade_price || 0;
      case 8: // Metro Retail VIP AUD Incl
        return inventoryItem.metro_retail_vip_aud_incl || inventoryItem.retail_price || 0;
      case 9: // Metro Trade VIP1 AUD Incl
        return inventoryItem.metro_trade_vip1_aud_incl || inventoryItem.trade_price || 0;
      case 10: // Metro Trade VIP2 AUD Incl
        return inventoryItem.metro_trade_vip2_aud_incl || inventoryItem.trade_price || 0;
      default:
        // Fall back to trade_price if price list ID is not recognized
        console.warn(`Unknown price list ID: ${customerPriceListId}, falling back to trade_price`);
        return inventoryItem.trade_price || 0;
    }
  };

  // Initialize form with provided data if available
  useEffect(() => {
    // Reset the ref when initialData changes
    storeTypeSetRef.current = false;
    
    if (initialData) {
      const processedInitialData = {
        ...initialData,
        totals: {
          ...initialData.totals,
          discount: initialData.totals.discount ?? 0 // Ensure discount defaults to 0
        }
      };
      
      setFormData(processedInitialData);
      setInitialFormData(JSON.parse(JSON.stringify(processedInitialData))); // Deep copy for proper baseline

      // Check if delivery address is "Customer Pickup" and set checkbox accordingly
      const isCustomerPickup = processedInitialData.deliverToAddress === 'Customer Pickup';
      setCustomerPickup(isCustomerPickup);

      // If initial data has both customer and quote, enable filtering
      if (initialData.invoiceTo && initialData.linkedQuote) {
        setFilterQuotesByCustomer(true);
      }

      // Initialize quote search term with the quote number if linkedQuote is provided
      // We'll handle this in a separate useEffect after quotes are loaded
      if (initialData.linkedQuote) {
        // Store the linkedQuote ID to set search term later when quotes are available
        setQuoteSearchTerm(''); // Clear initially, will be set when quotes load
      }

      // Pre-fetch individual SKU data for items in initial data
      if (initialData.items && initialData.items.length > 0) {
        console.log('Pre-fetching individual SKU data for initial items...');
        initialData.items.forEach(item => {
          if (item.sku) {
            fetchIndividualSkuData(item.sku);
          }
        });
      }
    } else {
      // Create default form data with one empty item (similar to Create New Quote)
      const newId = Math.random().toString(36).substring(2, 11);
      const initialItem: InvoiceItem = {
        id: newId,
        sku: '',
        description: '',
        quantity: {
          units: null,
          boxes: null,
          pieces: null,
          m2: null,
          blm: null,
          total: 0
        },
        unitPrice: 0,
        totalPrice: 0,
        deductFromStock: false
      };

      const defaultFormData = {
        storeType: getDefaultStoreType(storeFilter)?.name || '',
        saleType: '',
        modeOfPayment: '',
        poNumber: '',
        invoiceTo: '',
        deliverToAddress: '',
        invoiceNo: '',
        notes: '',
        linkedQuote: '',
        date: new Date().toISOString().split('T')[0],
        dontSendPo: false,
        status: 'saved', // Default status for new invoices
        items: [initialItem],
        totals: {
          orderTotal: 0,
          surcharge: 0,
          gst: 0,
          discount: 0,
          shipping: 0,
          grandTotal: 0
        }
      };

      // Set default store type for staff/manager users
      const defaultStore = getDefaultStoreType(storeFilter);
      if (defaultStore) {
        defaultFormData.storeType = defaultStore.name;
      }

      setFormData(defaultFormData);
      setInitialFormData(JSON.parse(JSON.stringify(defaultFormData))); // Deep copy for proper baseline
    }
    // Only depend on initialData to prevent infinite rerenders
    // quotes dependency removed to prevent loops
    // Note: storeFilter is intentionally not in dependencies to avoid re-running this effect
    // Store type updates are handled by the dedicated useEffect below
  }, [initialData, storeFilter.isStoreRestricted, storeFilter.userStoreType]);

  // Add useEffect to update store type when storeFilter changes (user data loads)
  // This runs on mount and whenever user data becomes available
  useEffect(() => {
    // Only update for new invoices (when initialData is not provided)
    // For existing invoices, keep the original store type
    if (!initialData && storeFilter.isStoreRestricted && storeFilter.userStoreType) {
      setFormData(prev => {
        // Always update if the store type is empty or doesn't match user's store type
        const shouldUpdate = !prev.storeType || 
                            prev.storeType === '' || 
                            prev.storeType !== storeFilter.userStoreType;
        
        if (shouldUpdate) {
          console.log('Updating store type from storeFilter:', storeFilter.userStoreType, 'Current:', prev.storeType);
          storeTypeSetRef.current = true;
          return {
            ...prev,
            storeType: storeFilter.userStoreType || ''
          };
        }
        return prev;
      });
    }
  }, [storeFilter.isStoreRestricted, storeFilter.userStoreType, storeFilter.userStoreTypeId, initialData, user?.store_type?.name, user?.store_type?.id]);
  
  // Additional effect to catch user data loading after component mount
  // This ensures we catch the update even if the above effect doesn't trigger
  useEffect(() => {
    if (!initialData && !storeTypeSetRef.current && user?.store_type?.name && (user.role === 'staff' || user.role === 'manager')) {
      console.log('Setting store type from user data (fallback):', user.store_type.name);
      setFormData(prev => {
        if (!prev.storeType || prev.storeType === '') {
          storeTypeSetRef.current = true;
          return {
            ...prev,
            storeType: user.store_type!.name
          };
        }
        return prev;
      });
    }
  }, [user?.store_type?.name, user?.role, initialData]);

  // Validate the linkedQuote whenever it changes
  useEffect(() => {
    if (formData?.linkedQuote && formData.linkedQuote.trim() !== '') {
      const isValidUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(formData.linkedQuote);

      if (!isValidUuid) {
        console.warn('Non-UUID quote ID detected:', formData.linkedQuote);
      }
    }
    // Using formData?.linkedQuote directly in the dependency array might cause rerenders
    // as formData is an object that changes on every render, even if linkedQuote is the same
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.linkedQuote]);

  // Separate useEffect to handle quote initialization after quotes are loaded
  useEffect(() => {
    if (formData.linkedQuote && quotes.length > 0 && !quoteSearchTerm) {
      const matchedQuote = quotes.find(quote => quote.id === formData.linkedQuote);
      if (matchedQuote) {
        setQuoteSearchTerm(matchedQuote.quote_no);
        console.log('Set quote search term from linked quote:', matchedQuote.quote_no);
              } else {
          // If we can't find the quote in the current quotes list, make an API call to get it
          const fetchLinkedQuote = async () => {
            try {
              const response: any = await apiClient.get(`${ENDPOINTS.QUOTES}/${formData.linkedQuote}`);
              if (response && response.quote_no) {
                setQuoteSearchTerm(response.quote_no);
                console.log('Set quote search term from API call:', response.quote_no);
              }
            } catch (error) {
              console.error('Error fetching linked quote details:', error);
            }
          };
          fetchLinkedQuote();
        }
    }
  }, [formData.linkedQuote, quotes.length, quoteSearchTerm]); // Use quotes.length instead of quotes array to prevent infinite loops

  // Recalculate totals when payment mode changes to update surcharge
  useEffect(() => {
    if (formData.items && formData.items.length > 0) {
      calculateTotals(formData.items);
    }
  }, [formData.modeOfPayment]); // Keep only the specific field that triggers recalculation

  // Initialize shipping input value when formData changes
  useEffect(() => {
    if (formData.totals?.shipping !== undefined) {
      setShippingInputValue(formData.totals.shipping.toString());
    }
  }, [formData.totals?.shipping]);

  // Fetch quotes for linking
  useEffect(() => {
    fetchQuotes();
    fetchStoreTypes();
    fetchInventoryItems(true); // Force refresh on initial load
  }, []);

  // Function to fetch quotes
  const fetchQuotes = async () => {
    setIsLoadingQuotes(true);
    try {
      // Fetch all current quotes with larger limit to get comprehensive data
      const response = await quoteService.getCurrentQuotes({ limit: 100 });

      if (response && response.items) {
        setQuotes(response.items.map(quote => ({
          id: quote.id,
          quote_no: quote.quote_no,
          items: quote.quote_items,
          customer_id: quote.customer_id,
          customer_name: quote.customer_name,
          store_type: quote.store_type
        })));
        console.log('Successfully loaded quotes with customer info:', response.items.length);
      } else {
        console.warn('Quote data was returned in an unexpected format');
        setQuotes([]);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      // Keep last known good state if there was an error
    } finally {
      setIsLoadingQuotes(false);
    }
  };

  // Function to fetch store types
  const fetchStoreTypes = async () => {
    setIsLoadingStoreTypes(true);
    try {
      const response = await storeTypeService.getStoreTypes();
      if (response && response.length > 0) {
        setStoreTypes(response);
      } else {
        console.warn('No store types returned from API, using defaults');
        // Set default store types if API returns empty array
        setStoreTypes([
          { id: 1, name: 'Cranbourne' },
          { id: 2, name: 'Sale' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching store types:', error);
      // Set default store types if API fails
      setStoreTypes([
        { id: 1, name: 'Cranbourne' },
        { id: 2, name: 'Sale' }
      ]);
    } finally {
      setIsLoadingStoreTypes(false);
    }
  };

  // Function to fetch inventory items for SKU dropdown
  const fetchInventoryItems = async (force: boolean = false) => {
    setIsLoadingInventory(true);
    try {
      // Clear cache if force refreshing to ensure fresh data
      if (force) {
        inventoryService.clearInventoryCache();
        // Also clear individual SKU data when doing a force refresh
        setIndividualSkuData({});
      }

      // Just load a minimal set of items for initial display and item lookup
      // Full search will be handled by the backend search
      const response = await inventoryService.getInventoryItems({ limit: 20, active_only: true });
      
      if (response && response.length > 0) {
        console.log('Fetched minimal inventory item set:', response.length);
        setInventoryItems(response);
      } else {
        console.warn('Failed to fetch initial inventory items');
        setInventoryItems([]);
      }

      // Update refresh timestamp when force refreshing
      if (force) {
        setLastInventoryRefresh(Date.now());
      }
    } catch (error: any) {
      console.error('Error fetching inventory items:', error);
      setInventoryItems([]);
    } finally {
      setIsLoadingInventory(false);
    }
  };

  // Function to load a quote's items into the invoice
  const loadQuoteItems = async (quoteId: string) => {
    if (!quoteId) return;

    try {
      console.log(`Loading quote items for quote ID: ${quoteId}`);

      // Use the specific API endpoint for getting quote details
      const quote: any = await apiClient.get(`${ENDPOINTS.QUOTES}/${quoteId}`);

      if (!quote) {
        console.error('Quote not found:', quoteId);
        return;
      }

      console.log('Quote data loaded:', quote);

      // Convert quote items to invoice items format - handling both possible API response structures
      const quoteItems: QuoteItemAPI[] = (quote.items || quote.quote_items || []) as QuoteItemAPI[];

      const invoiceItems = quoteItems.length > 0
        ? quoteItems.map((item: QuoteItemAPI) => ({
          id: String(Math.random()),
          sku: item.sku_code || item.sku || '',
          description: item.description || '',
          quantity: {
            units: item.quantity_units || item.units || 0,
            boxes: item.quantity_boxes || item.boxes || 0,
            pieces: item.quantity_pieces || item.pieces || 0,
            m2: item.quantity_m2 || item.m2 || 0,
            blm: item.quantity_blm || item.blm || 0,
            total: (item.quantity_units || item.units || 0) +
              (item.quantity_boxes || item.boxes || 0) +
              (item.quantity_pieces || item.pieces || 0) +
              (item.quantity_m2 || item.m2 || 0)
              // BLM is excluded from total calculation as per requirements
          },
          unitPrice: item.unit_price || 0,
          totalPrice: item.totalPrice || item.total_price ||
            (item.unit_price || 0) * ((item.quantity_units || item.units || 0) +
              (item.quantity_boxes || item.boxes || 0) +
              (item.quantity_pieces || item.pieces || 0) +
              (item.quantity_m2 || item.m2 || 0)),
          deductFromStock: true
        }))
        : [];

      console.log('Transformed invoice items:', invoiceItems);

      // Update SKU search terms for the new items
      const newSkuSearchTerms = { ...skuSearchTerms };
      invoiceItems.forEach((item, index) => {
        newSkuSearchTerms[index] = item.sku;
      });
      setSKUSearchTerms(newSkuSearchTerms);

      // Update form data with quote information
      setFormData(prev => {
        // Calculate the new totals based on the items
        const orderTotal = invoiceItems.reduce((sum, item) => sum + item.totalPrice, 0);
        
        // Calculate discount amount - applies only to item total (orderTotal)
        const discount = prev.totals.discount || 0;
        const shipping = prev.totals.shipping || 0;
        const discountAmount = orderTotal * (discount / 100);
        
        // Calculate subtotal after discount
        const subtotalAfterDiscount = orderTotal - discountAmount;
        
        // Calculate GST = 10% of (Total - Discount + Shipping)
        const gst = (subtotalAfterDiscount + shipping) * 0.1;

        // Calculate credit card surcharge (1.5% of subtotal after discount + shipping + gst) only if payment mode is Credit Card
        const surcharge = prev.modeOfPayment === 'Credit Card' ? (subtotalAfterDiscount + shipping + gst) * 0.015 : 0;
        
        // Calculate grand total = subtotalAfterDiscount + shipping + gst + surcharge
        const grandTotal = subtotalAfterDiscount + shipping + gst + surcharge;

        return {
          ...prev,
          storeType: quote.store_type_id || quote.store_type || prev.storeType,
          deliverToAddress: quote.deliver_to_address || prev.deliverToAddress,
          items: invoiceItems.length > 0 ? invoiceItems : prev.items,
          totals: {
            ...prev.totals,
            orderTotal: orderTotal,
            surcharge: surcharge,
            gst: gst,
            grandTotal: grandTotal
          }
        };
      });

      // If we have customer information in the quote, set it
      if (quote.customer_id) {
        try {
          const customer = await customerService.getCustomerById(quote.customer_id);
          setSelectedCustomer(customer);
          console.log('Selected customer from quote:', customer);
        } catch (error) {
          console.error('Error fetching customer details from quote:', error);
        }
      }

      // Update baseline after loading quote items since this is a legitimate change
      setTimeout(() => {
        updateBaseline();
      }, 100);
    } catch (error) {
      console.error('Error loading quote:', error);
      // Keep the form in its current state if there was an error
    }
  };

  // Use effect to fetch necessary data
  useEffect(() => {
    fetchQuotes();
    fetchStoreTypes();
    fetchInventoryItems(true); // Force refresh on initial load
    // These functions don't depend on any state that changes, so they only need to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load customer details when invoiceTo is set (from initialData or user selection)
  useEffect(() => {
    if (formData.invoiceTo && (!selectedCustomer || selectedCustomer.id !== formData.invoiceTo)) {
      const loadCustomer = async () => {
        try {
          const customer = await customerService.getCustomerById(formData.invoiceTo);
          setSelectedCustomer(customer);
          console.log('Loaded customer from invoiceTo:', customer);
          console.log('Customer price_list_id:', customer?.price_list_id);
          
          // Re-fetch pricing for existing SKUs when customer is loaded
          if (customer && formData.items.some(item => item.sku && item.sku.trim())) {
            refetchPricingForExistingSkus(customer);
          }
        } catch (error) {
          console.error('Error loading customer from invoiceTo:', error);
          setSelectedCustomer(null);
        }
      };
      
      loadCustomer();
    } else if (!formData.invoiceTo) {
      // Clear selected customer if invoiceTo is cleared
      setSelectedCustomer(null);
    }
  }, [formData.invoiceTo]);

  // Re-fetch individual SKU data when form items change (for persistence across refreshes)
  useEffect(() => {
    if (formData.items && formData.items.length > 0) {
      formData.items.forEach((item, index) => {
        if (item.sku && !individualSkuData[item.sku]) {
          // Only fetch if we don't already have the data
          fetchIndividualSkuData(item.sku).then(() => {
            // After fetching, update deductFromStock if there's a quantity
            const totalQuantity = item.quantity?.total || 0;
            if (totalQuantity > 0) {
              setTimeout(() => {
                updateDeductFromStockBasedOnAvailability(index, item.sku, totalQuantity);
              }, 200);
            }
          });
        }
      });
    }
  }, [formData.items, individualSkuData, fetchIndividualSkuData, updateDeductFromStockBasedOnAvailability]);

  // Auto-refresh inventory data every 5 minutes to pick up newly uploaded images (FIXED: Reduced frequency)
  useEffect(() => {
    // Only set up auto-refresh if user is actively working on the page
    const interval = setInterval(() => {
      // Only auto-refresh if page is visible, not currently loading, not submitting, and user has interacted recently
      if (!document.hidden && !isLoadingInventory && !isSubmitting && document.hasFocus()) {
        if (process.env.NODE_ENV === 'development') console.log('Auto-refreshing inventory data...');
        fetchInventoryItems(true);
      }
    }, 300000); // FIXED: Changed from 30 seconds to 5 minutes (300000ms)

    return () => clearInterval(interval);
  }, [isLoadingInventory, isSubmitting]); // Add dependencies to prevent excessive calls

  // Refresh inventory when page becomes visible (user returns from other tabs/windows)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isLoadingInventory && !isSubmitting) {
        console.log('Page became visible, refreshing inventory data...');
        fetchInventoryItems(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [isLoadingInventory, isSubmitting]); // Add dependencies

  // Handle outside clicks for quote dropdown and SKU dropdowns
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is inside any dropdown container
      const isInsideContainer = Array.from(document.querySelectorAll('.dropdown-container')).some(container => 
        container.contains(target)
      );
      
      // Check if click is inside any SKU dropdown
      const isInsideDropdown = Array.from(document.querySelectorAll('[data-sku-dropdown]')).some(dropdown => 
        dropdown.contains(target)
      );
      
      // Check if click is inside the quote dropdown
      const isInsideQuoteDropdown = quoteDropdownRef.current && quoteDropdownRef.current.contains(target);
      
      if (!isInsideContainer && !isInsideDropdown) {
        setOpenSKUDropdowns({});
        setSKUDropdownPositions({});
      }
      
      // Close quote dropdown if click is outside
      if (!isInsideQuoteDropdown) {
        setIsQuoteDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Handle quote selection
  const handleQuoteSelect = (quote: Quote) => {
    console.log("Selected quote:", quote);
    setFormData(prev => ({
      ...prev,
      linkedQuote: quote.id
    }));
    setQuoteSearchTerm(quote.quote_no);
    setIsQuoteDropdownOpen(false);
    
    // Auto-fill customer if quote has customer information and current customer is empty or different
    if (quote.customer_id && (!formData.invoiceTo || quote.customer_id !== formData.invoiceTo)) {
      setFormData(prev => ({
        ...prev,
        invoiceTo: quote.customer_id!
      }));

      // Enable filtering when customer is auto-filled from quote
      setFilterQuotesByCustomer(true);

      // Fetch customer details for the auto-filled customer
      if (quote.customer_id) {
        const fetchCustomerDetails = async () => {
          try {
            const customer = await customerService.getCustomerById(quote.customer_id!);
            console.log('Fetched customer details:', customer);
            console.log('Customer price list info:', {
              price_list_id: customer.price_list_id,
              price_list: customer.price_list
            });
            setSelectedCustomer(customer);
            console.log('Selected customer with price list:', customer);
          } catch (error) {
            console.error('Error fetching customer details for auto-fill:', error);
            setSelectedCustomer(null);
          }
        };
        fetchCustomerDetails();
      }
    }

    // Auto-fill store type and other details from quote
    const fetchQuoteDetails = async () => {
      try {
        const quoteDetails: any = await apiClient.get(`${ENDPOINTS.QUOTES}/${quote.id}`);
        console.log("Fetched quote details:", quoteDetails);
        
        if (quoteDetails) {
          // Auto-populate form data from the selected quote
          setFormData(prev => ({
            ...prev,
            storeType: quoteDetails.store_type || prev.storeType,
            deliverToAddress: quoteDetails.deliver_to_address || prev.deliverToAddress,
            // Set default sale type and payment mode if not already set
            saleType: prev.saleType || 'Trade',
            modeOfPayment: prev.modeOfPayment || 'Cash'
          }));
          
          console.log('Auto-filled data from quote:', {
            storeType: quoteDetails.store_type,
            deliverToAddress: quoteDetails.deliver_to_address
          });
          
          // Load the quote items
          loadQuoteItems(quote.id);
        }
      } catch (error) {
        console.error('Error fetching quote details:', error);
        // Still try to load items even if details fetch fails
        loadQuoteItems(quote.id);
      }
    };

    // Fetch quote details to get store type and other information
    fetchQuoteDetails();
  };

  // Handle SKU selection
  const handleSKUSelect = (index: number, item: any) => {
    console.log('Selected SKU:', item);
    // Use either sku_code or sku field, depending on which is available
    const skuCode = item.sku_code || item.sku;
    handleItemChange(index, 'sku', skuCode);
    setSKUSearchTerms(prev => ({
      ...prev,
      [index]: skuCode
    }));
    setOpenSKUDropdowns(prev => ({
      ...prev,
      [index]: false
    }));
  };

  const handleSKUBlur = (index: number) => {
    // Revert search term to the actually selected item SKU if it's different
    const item = formData.items[index];
    const searchTerm = skuSearchTerms[index];
    
    if (searchTerm !== item.sku) {
      setSKUSearchTerms(prev => ({
        ...prev,
        [index]: item.sku || ''
      }));
    }
    
    // Delay closing to allow clicking on dropdown items
    setTimeout(() => {
      setOpenSKUDropdowns(prev => ({
        ...prev,
        [index]: false
      }));
    }, 200);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if shipping is blank or zero
    const shipping = formData.totals?.shipping || 0;
    const isShippingBlank = shipping === 0 || shipping === null || shipping === undefined || String(shipping).trim() === '';

    if (isShippingBlank) {
      // Show shipping confirmation dialog
      setPendingSubmit(() => async () => {
        setIsSubmitting(true);
        try {
          // Ensure store type is set for restricted users
          const submitData = { ...formData };
          if (storeFilter.isStoreRestricted && storeFilter.userStoreType && !submitData.storeType) {
            console.log('Setting store type for restricted user before submit:', storeFilter.userStoreType);
            submitData.storeType = storeFilter.userStoreType;
          }
          
          // Call the parent's onSubmit handler with the form data
          // The parent will handle the actual API call
          await onSubmit(submitData);
          setHasUnsavedChanges(false);
        } catch (error: any) {
          console.error('Error submitting invoice:', error);
          setIsSubmitting(false);
          alert('There was an error submitting the invoice. Please try again.');
        }
      });
      setShowShippingConfirmation(true);
      return;
    }

    // If shipping is provided, proceed with submission
    setIsSubmitting(true);
    try {
      // Ensure store type is set for restricted users
      const submitData = { ...formData };
      if (storeFilter.isStoreRestricted && storeFilter.userStoreType && !submitData.storeType) {
        console.log('Setting store type for restricted user before submit:', storeFilter.userStoreType);
        submitData.storeType = storeFilter.userStoreType;
      }
      
      // Call the parent's onSubmit handler with the form data
      // The parent will handle the actual API call
      await onSubmit(submitData);
      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error('Error submitting invoice:', error);
      setIsSubmitting(false);
      alert('There was an error submitting the invoice. Please try again.');
    }
  };

  const handleShippingConfirm = () => {
    setShowShippingConfirmation(false);
    if (pendingSubmit) {
      pendingSubmit();
      setPendingSubmit(null);
    }
  };

  const handleShippingCancel = () => {
    setShowShippingConfirmation(false);
    setPendingSubmit(null);
  };

  // Handle save as draft
  const handleSaveAsDraftInternal = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Ensure store type is set for restricted users
    const submitData = { ...formData };
    if (storeFilter.isStoreRestricted && storeFilter.userStoreType && !submitData.storeType) {
      console.log('Setting store type for restricted user before save as draft:', storeFilter.userStoreType);
      submitData.storeType = storeFilter.userStoreType;
    }
    
    onSaveAsDraft(submitData);
  };

  const handleCancel = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    // Check if form has unsaved changes - always check, even if initialFormData is null
    const hasChanges = hasFormDataChanged();
    
    // If initialFormData is null, check if form has any meaningful data
    if (!initialFormData) {
      // Check if form has any data entered
      const hasAnyData = 
        formData.storeType !== '' ||
        formData.saleType !== '' ||
        formData.modeOfPayment !== '' ||
        formData.poNumber !== '' ||
        formData.invoiceTo !== '' ||
        formData.deliverToAddress !== '' ||
        formData.notes !== '' ||
        formData.linkedQuote !== '' ||
        (formData.items && formData.items.length > 0 && formData.items.some(item => item.sku !== ''));
      
      if (hasAnyData) {
        setShowExitConfirmation(true);
        return;
      }
    }
    
    if (hasChanges) {
      setShowExitConfirmation(true);
    } else {
      setHasUnsavedChanges(false);
      onCancel();
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    setHasUnsavedChanges(false);
    onCancel();
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
  };

  // Add shipping input handler
  const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Only allow numeric characters, decimal point, and empty string
    const numericRegex = /^(\d*\.?\d*)$/;
    if (inputValue === '' || numericRegex.test(inputValue)) {
      setShippingInputValue(inputValue);
      
      // Convert to number for calculations (empty string becomes 0)
      const numericValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
      
      // Update formData with the new shipping value
      setFormData(prev => {
        const orderTotal = prev.totals.orderTotal;
        const discount = prev.totals.discount;
        
        // Calculate discount amount - applies only to item total (orderTotal)
        const discountAmount = orderTotal * (discount / 100);
        
        // Calculate subtotal after discount
        const subtotalAfterDiscount = orderTotal - discountAmount;
        
        // Calculate GST = 10% of (Total - Discount + Shipping)
        const gst = (subtotalAfterDiscount + numericValue) * 0.1;

        // Calculate credit card surcharge (1.5% of subtotal after discount + shipping + gst) only if payment mode is Credit Card
        const surcharge = prev.modeOfPayment === 'Credit Card' ? (subtotalAfterDiscount + numericValue + gst) * 0.015 : 0;
        
        // Calculate grand total = subtotalAfterDiscount + shipping + gst + surcharge
        const grandTotal = subtotalAfterDiscount + numericValue + gst + surcharge;
        
        return {
          ...prev,
          totals: {
            ...prev.totals,
            shipping: numericValue,
            surcharge: surcharge,
            gst: gst,
            grandTotal: grandTotal
          }
        };
      });
    }
  };



  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty input or valid decimal numbers
    if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
      // Handle empty input - set to 0
      const numericValue = inputValue === '' ? 0 : parseFloat(inputValue);
      
      // Ensure discount is between 0 and 100
      const discountPercentage = Math.min(100, Math.max(0, numericValue));
      
      setFormData(prev => {
        const orderTotal = prev.totals.orderTotal;
        const shipping = prev.totals.shipping;
        
        // Calculate discount amount - applies only to item total (orderTotal)
        const discountAmount = orderTotal * (discountPercentage / 100);
        
        // Calculate subtotal after discount
        const subtotalAfterDiscount = orderTotal - discountAmount;
        
        // Calculate GST = 10% of (Total - Discount + Shipping)
        const gst = (subtotalAfterDiscount + shipping) * 0.1;

        // Calculate credit card surcharge (1.5% of subtotal after discount + shipping + gst) only if payment mode is Credit Card
        const surcharge = prev.modeOfPayment === 'Credit Card' ? (subtotalAfterDiscount + shipping + gst) * 0.015 : 0;
        
        // Calculate grand total = subtotalAfterDiscount + shipping + gst + surcharge
        const grandTotal = subtotalAfterDiscount + shipping + gst + surcharge;
        
        return {
          ...prev,
          totals: {
            ...prev.totals,
            discount: discountPercentage,
            surcharge: surcharge,
            gst: gst,
            grandTotal: grandTotal
          }
        };
      });
    }
  };

  // Format currency for display


  // Filter quotes based on search term and optionally by customer
  const filteredQuotes = quotes.filter(quote => {
    // First apply search term filter
    const matchesSearch = quoteSearchTerm.trim() === '' || 
      quote.quote_no.toLowerCase().includes(quoteSearchTerm.toLowerCase()) ||
      quote.id.toLowerCase().includes(quoteSearchTerm.toLowerCase());

    // If a customer is selected and filtering is enabled, only show quotes for that customer
    if (filterQuotesByCustomer && formData.invoiceTo) {
      return matchesSearch && quote.customer_id === formData.invoiceTo;
    }

    return matchesSearch;
  });



  // Add state for filtered inventory items by row
  const [filteredInventoryItemsByRow, setFilteredInventoryItemsByRow] = useState<{ [key: number]: any[] }>({});

  // Use debounce to prevent excessive API calls
  const debouncedSearchSKUs = useMemo(
    () => debounce(async (searchTerm: string, rowIndex: number) => {
      if (!searchTerm.trim()) {
        setFilteredInventoryItemsByRow(prev => ({
          ...prev,
          [rowIndex]: []
        }));
        return;
      }

      setIsLoadingInventory(true);
      try {
        const results = await inventoryService.searchSKUs(searchTerm, 10);
        setFilteredInventoryItemsByRow(prev => ({
          ...prev,
          [rowIndex]: results
        }));
      } catch (error) {
        console.error('Error searching SKUs:', error);
      } finally {
        setIsLoadingInventory(false);
      }
    }, 300),
    []
  );

  // Update effect for sku search terms
  useEffect(() => {
    // When search terms change, trigger backend search
    Object.entries(skuSearchTerms).forEach(([rowIndexStr, searchTerm]) => {
      const rowIndex = parseInt(rowIndexStr);
      
      if (openSKUDropdowns[rowIndex] && searchTerm?.trim()) {
        debouncedSearchSKUs(searchTerm, rowIndex);
      }
    });
  }, [skuSearchTerms, openSKUDropdowns, debouncedSearchSKUs]);

  // Update SKU search terms when initialData changes
  useEffect(() => {
    if (initialData?.items) {
      const initialTerms: { [key: number]: string } = {};
      initialData.items.forEach((item, index) => {
        initialTerms[index] = item.sku || '';
      });
      setSKUSearchTerms(initialTerms);
    }
  }, [initialData]);

  // Check if PO Number should be disabled
  const isPONumberDisabled = formData.saleType.toLowerCase() === 'store';

  // Function to re-fetch pricing for existing SKUs when customer changes (ONLY use API price - NO FALLBACKS)
  const refetchPricingForExistingSkus = async (customer: any) => {
    console.log('Re-fetching pricing for existing SKUs with new customer:', customer?.company_name || customer?.name);
    
    const updatedItems = [...formData.items];
    let hasUpdates = false;

    for (let i = 0; i < updatedItems.length; i++) {
      const item = updatedItems[i];
      
      // Only re-fetch if SKU exists
      if (item.sku) {
        console.log(`Re-fetching pricing for existing SKU: ${item.sku}`);
        
        try {
          const supplierPricingResponse = await supplierService.getSkuPricing(item.sku);
          console.log(`Re-fetch supplier pricing response for SKU ${item.sku}:`, supplierPricingResponse);

          const customerPriceListId = customer?.price_list_id;
          
          if (supplierPricingResponse.success && Object.keys(supplierPricingResponse.price_list).length > 0) {
            console.log(`Re-fetch: Customer price list ID: ${customerPriceListId}`);
            console.log(`Re-fetch: Available prices in supplier response:`, supplierPricingResponse.price_list);

            // Check if we have a valid customer price list ID and corresponding price
            if (customerPriceListId && supplierPricingResponse.price_list[customerPriceListId.toString()]) {
              const priceForCustomer = supplierPricingResponse.price_list[customerPriceListId.toString()];

              console.log(`Re-fetch: Found price for customer price list ID ${customerPriceListId}: $${priceForCustomer} for SKU ${item.sku}`);
              
              // Update unit price with customer price list value - ONLY use API price, NO FALLBACK
              updatedItems[i].unitPrice = priceForCustomer;
              updatedItems[i].totalPrice = updatedItems[i].quantity.total * priceForCustomer; // Exclude GST
              hasUpdates = true;

              console.log(`Re-fetch SKU ${item.sku}: Updated price to ${priceForCustomer}`);
            } else {
              // No matching price found - do NOT use fallback, leave at current value or 0
              console.warn(`Re-fetch SKU ${item.sku}: No pricing found for customer price list ID ${customerPriceListId}`);
              console.warn(`Available price list keys:`, Object.keys(supplierPricingResponse.price_list));
              
              if (!customerPriceListId) {
                console.warn(`Customer selected but no price_list_id set - cannot determine which price to use`);
              } else {
                console.warn(`Customer price_list_id ${customerPriceListId} not found in available prices`);
              }
              
              // DO NOT use fallback - leave price at current value (or 0 if it was 0)
              // Don't update hasUpdates since we're not changing the price
            }
          } else {
            // No supplier pricing found - do NOT use fallback
            console.warn(`Re-fetch SKU ${item.sku}: No supplier pricing found in API response`);
            console.warn(`Response success: ${supplierPricingResponse.success}`);
            console.warn(`Price list keys:`, Object.keys(supplierPricingResponse.price_list || {}));
            
            // DO NOT use fallback - leave price at current value
          }
        } catch (error) {
          console.error(`Error re-fetching pricing for SKU ${item.sku}:`, error);
          console.error('DO NOT use fallback - only use correct price from API');
          
          // DO NOT use fallback on error - leave price at current value
        }
      }
    }

    // Update state if any prices were updated
    if (hasUpdates) {
      console.log(' Updated pricing for existing SKUs after customer selection');
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
      calculateTotals();
    }
  };

  // Add handler for customer selection
  const handleCustomerSelect = (customerId: string, customerName: string) => {
    setFormData(prev => ({
      ...prev,
      invoiceTo: customerId // Store the customer ID
    }));

    // Enable quote filtering by customer
    setFilterQuotesByCustomer(true);

    // Clear quote selection when customer changes
    setFormData(prev => ({
      ...prev,
      linkedQuote: ''
    }));
    setQuoteSearchTerm('');

    // Fetch customer details to get price list information (CRITICAL for pricing)
    const fetchCustomerDetails = async () => {
      try {
        const customer = await customerService.getCustomerById(customerId);
        setSelectedCustomer(customer);
        console.log('Selected customer with price list:', customer);
        console.log('Customer price_list_id:', customer?.price_list_id);
        
        // Re-fetch pricing for existing SKUs when customer is selected
        if (customer && formData.items.some(item => item.sku && item.sku.trim())) {
          refetchPricingForExistingSkus(customer);
        }
      } catch (error) {
        console.error('Error fetching customer details:', error);
        setSelectedCustomer(null);
      }
    };

    fetchCustomerDetails();
  };

  // Clear items when customer is removed
  useEffect(() => {
    // If invoiceTo becomes empty and there are items with SKUs, reset to one empty item
    if ((!formData.invoiceTo || formData.invoiceTo.trim() === '') && formData.items.length > 0) {
      const hasItemsWithSku = formData.items.some(item => item.sku && item.sku.trim() !== '');
      
      if (hasItemsWithSku) {
        // Reset to one empty item (similar to Create New Quote)
        const newId = Math.random().toString(36).substring(2, 11);
        const emptyItem: InvoiceItem = {
          id: newId,
          sku: '',
          description: '',
          quantity: {
            units: null,
            boxes: null,
            pieces: null,
            m2: null,
            blm: null,
            total: 0
          },
          unitPrice: 0,
          totalPrice: 0,
          deductFromStock: false
        };
        
        setFormData(prev => ({
          ...prev,
          items: [emptyItem]
        }));
      }
    }
  }, [formData.invoiceTo]);

  // Add function to handle shipping modal
  const handleShippingInfoClick = () => {
    setIsShippingModalOpen(true);
  };

  const handleCloseShippingModal = () => {
    setIsShippingModalOpen(false);
  };

  const handleCartonInfoHover = (itemIndex: number, show: boolean) => {
    setCartonInfoTooltip({
      visible: show,
      itemIndex: show ? itemIndex : null
    });
  };

  // Handle shipping input blur to format currency
  const handleShippingBlur = () => {
    // Only format if there's a value greater than 0
    if (formData.totals.shipping > 0) {
      setShippingInputValue(formData.totals.shipping.toString());
    } else {
      setShippingInputValue('');
    }
  };

  // Function to update dropdown position
  const updateDropdownPosition = (index: number) => {
    const inputElement = skuInputRefs.current[index];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setSKUDropdownPositions(prev => ({
        ...prev,
        [index]: {
          top: rect.bottom + window.scrollY + 2,
          left: rect.left + window.scrollX,
          width: Math.max(rect.width, 200)
        }
      }));
    }
  };

  // Handle scroll and resize events to update dropdown positions
  useEffect(() => {
    const handleScroll = () => {
      Object.keys(openSKUDropdowns).forEach(key => {
        const index = parseInt(key);
        if (openSKUDropdowns[index]) {
          updateDropdownPosition(index);
        }
      });
    };

    const handleResize = () => {
      Object.keys(openSKUDropdowns).forEach(key => {
        const index = parseInt(key);
        if (openSKUDropdowns[index]) {
          updateDropdownPosition(index);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [openSKUDropdowns]);

  // Form field change handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // If the linked quote changes, load its items
    if (name === 'linkedQuote' && value) {
      loadQuoteItems(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  // Handle customer pickup checkbox
  const handleCustomerPickupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setCustomerPickup(isChecked);
    
    if (isChecked) {
      setFormData(prev => ({
        ...prev,
        deliverToAddress: 'Customer Pickup'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        deliverToAddress: ''
      }));
    }
  };

  // Add helper function to determine which quantity field is active
  const getActiveQuantityField = (quantity: InvoiceItemQuantity, item?: InvoiceItem): string | null => {
    if (quantity.units && quantity.units > 0) return 'units';
    // Don't treat boxes as active if they were set programmatically from M2 rounding
    if (quantity.boxes && quantity.boxes > 0 && !(item as any)?.boxesFromM2Rounding) return 'boxes';
    if (quantity.pieces && quantity.pieces > 0) return 'pieces';
    
    // Check if BLM is active first since it should override M2
    // Only consider BLM active if it has a meaningful value AND M2 is calculated
    if (quantity.blm && quantity.blm > 0 && quantity.m2 && quantity.m2 > 0) return 'blm';
    
    // Only consider M2 active if BLM is not active
    if (quantity.m2 && quantity.m2 > 0 && (!quantity.blm || quantity.blm === 0)) return 'm2';
    
    return null;
  };

  // Item-specific changes
  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...formData.items];

    if (field.startsWith('quantity.')) {
      const quantityField = field.split('.')[1];
      
      // Handle empty string or invalid input
      if (value === '' || value === null || value === undefined) {
        // Special handling for BLM field - when BLM is cleared, also clear M2
        if (quantityField === 'blm') {
          newItems[index].quantity = {
            ...newItems[index].quantity,
            blm: null,
            m2: null // Clear M2 when BLM is cleared
          };
        } else {
          newItems[index].quantity = {
            ...newItems[index].quantity,
            [quantityField]: null
          };
        }
        
        // Clear M2 rounding result if M2 field is cleared
        if (quantityField === 'm2') {
          newItems[index].m2RoundingResult = undefined;
        }
        
        // Recalculate total
        let total = 0;
        if (newItems[index].quantity.units) total += Number(newItems[index].quantity.units);
        if (newItems[index].quantity.boxes) total += Number(newItems[index].quantity.boxes);
        if (newItems[index].quantity.pieces) total += Number(newItems[index].quantity.pieces);
        if (newItems[index].quantity.m2) total += Number(newItems[index].quantity.m2);
        
        newItems[index].quantity.total = total;
        newItems[index].totalPrice = total * newItems[index].unitPrice; // Exclude GST
        
        setTimeout(() => {
          const newTotals = calculateTotals(newItems);
          setFormData(prev => ({
            ...prev,
            items: newItems,
            totals: newTotals
          }));
        }, 0);
        return;
      }
      
      // For M2 field, allow any string input (including partial numbers like "1.")
      if (quantityField === 'm2') {
        // Store the raw value to allow typing
        newItems[index].quantity.m2 = value;
        
        // Only process if it's a valid number
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue) && numericValue > 0) {
          // Apply M2 carton rounding logic
          let finalQuantity = numericValue;
          let roundingResult: M2RoundingResult | undefined;

          if (newItems[index].cartonConstraint?.hasConstraints) {
            roundingResult = calculateM2Rounding(numericValue, newItems[index].cartonConstraint!);
            finalQuantity = roundingResult.roundedM2;
            newItems[index].m2RoundingResult = roundingResult;
          }

          // Calculate boxes based on M2 rounding
          let calculatedBoxes = null;
          if (roundingResult && roundingResult.cartonCount > 0) {
            calculatedBoxes = roundingResult.cartonCount;
          }

          // Clear other fields when M2 is active
          newItems[index].quantity = {
            units: null,
            boxes: calculatedBoxes || null,
            pieces: null,
            m2: value, // Keep the raw input value
            blm: null,
            total: finalQuantity
          };

          // Store whether boxes was set programmatically
          if (calculatedBoxes !== null) {
            newItems[index].boxesFromM2Rounding = true;
          }
        } else {
          // Invalid number, just update the field but don't clear others
          newItems[index].quantity.total = 0;
        }
        
        newItems[index].totalPrice = newItems[index].quantity.total * newItems[index].unitPrice; // Exclude GST
        
        setTimeout(() => {
          const newTotals = calculateTotals(newItems);
          setFormData(prev => ({
            ...prev,
            items: newItems,
            totals: newTotals
          }));
        }, 0);
        return;
      }
      
      // For other fields, use the original logic
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && numericValue > 0) {
        let finalQuantity = numericValue;
        let roundingResult: M2RoundingResult | undefined;

        // Apply M2 carton rounding if this is an M2 quantity change
        if (quantityField === 'm2' && newItems[index].cartonConstraint?.hasConstraints) {
          roundingResult = calculateM2Rounding(numericValue, newItems[index].cartonConstraint!);
          finalQuantity = roundingResult.roundedM2;
          newItems[index].m2RoundingResult = roundingResult;
        }

        // Handle BLM: calculate M2 equivalent and set it in M2 field
        if (quantityField === 'blm') {
          const m2Equivalent = numericValue * 3.66;
          newItems[index].quantity = {
            units: null,
            boxes: null,
            pieces: null,
            m2: m2Equivalent, // Auto-fill M2 with BLM conversion
            blm: numericValue,
            total: m2Equivalent // Total should be the M2 equivalent
          };
        } else {
          // Calculate boxes based on M2 rounding
          let calculatedBoxes = null;
          if (quantityField === 'm2' && roundingResult && roundingResult.cartonCount > 0) {
            calculatedBoxes = roundingResult.cartonCount;
        }

        newItems[index].quantity = {
          units: quantityField === 'units' ? numericValue : null,
            boxes: quantityField === 'boxes' ? numericValue : (calculatedBoxes || null),
          pieces: quantityField === 'pieces' ? numericValue : null,
          m2: quantityField === 'm2' ? numericValue : null, // Store original user input, not rounded value
            blm: quantityField === 'blm' ? null : newItems[index].quantity.blm, // Clear BLM when other fields are active (except when setting BLM)
          total: finalQuantity // Use rounded value for total calculations
        };

          // Store whether boxes was set programmatically for M2 rounding
          if (calculatedBoxes !== null) {
            newItems[index].boxesFromM2Rounding = true;
          } else if (quantityField === 'boxes') {
            newItems[index].boxesFromM2Rounding = false;
          }
        }
      } else {
        // If clearing the current field, just update that field
        newItems[index].quantity = {
          ...newItems[index].quantity,
          [quantityField]: numericValue
        };

        // Clear M2 rounding result if M2 field is cleared
        if (quantityField === 'm2') {
          newItems[index].m2RoundingResult = undefined;
        }

        // Recalculate total if no specific field is active
        let total = 0;
        if (newItems[index].quantity.units) total += Number(newItems[index].quantity.units);
        if (newItems[index].quantity.boxes) total += Number(newItems[index].quantity.boxes);
        if (newItems[index].quantity.pieces) total += Number(newItems[index].quantity.pieces);
        if (newItems[index].quantity.m2) total += Number(newItems[index].quantity.m2);
        // Note: M2 includes BLM conversion when BLM is active

        newItems[index].quantity.total = total;
      }
    } else if (field === 'sku') {
      newItems[index].sku = value;
    // Clear stale description and notes immediately when SKU changes so UI updates instantly
    newItems[index].description = '';
    newItems[index].notes = '';

      // If SKU is entered, fetch data from both inventory and supplier sources
      if (value && value.trim().length > 0) {
        // Debug pricing for Alex Group
        if (selectedCustomer && selectedCustomer.name) {
          debugPricingIssue(value, selectedCustomer.name);
        }

        // Step 1: Get SKU details from Inventory (for description, image, etc.)
        const inventoryItem = getInventoryItemData(value) || inventoryItems.find(item => item.sku_code === value);
        if (inventoryItem) {
          // Update description from inventory (will be overridden by supplier description if available)
          newItems[index].description = inventoryItem.description || inventoryItem.style_code || '';
          
          // Extract and store carton constraints
          const cartonConstraint = getCartonConstraints(inventoryItem);
          newItems[index].cartonConstraint = cartonConstraint;
          setCartonConstraints(prev => ({
            ...prev,
            [value]: cartonConstraint
          }));
          
          console.log(`EditInvoiceForm - Carton constraints for SKU ${value}:`, cartonConstraint);
        } else {
          // Clear carton constraints if no inventory item found
          newItems[index].cartonConstraint = undefined;
          setCartonConstraints(prev => {
            const newConstraints = { ...prev };
            delete newConstraints[value];
            return newConstraints;
          });
        }

        // Step 2: Get pricing from Supplier data (ONLY source for pricing - NO FALLBACKS)
        const fetchSupplierPricing = async () => {
          try {
            console.log(`Fetching supplier pricing for SKU: ${value}`);
            console.log(`Customer context: ${selectedCustomer ? (selectedCustomer.company_name || selectedCustomer.name) : 'NO CUSTOMER SELECTED'}`);
            
            const supplierPricingResponse = await supplierService.getSkuPricing(value);

            console.log(`Supplier pricing response for SKU ${value}:`, supplierPricingResponse);

            // Get customer price list ID BEFORE processing response
            const customerPriceListId = selectedCustomer?.price_list_id;
            
            // Enhanced debugging for Alex Group
            debugAlexGroupPricing(value, customerPriceListId, supplierPricingResponse);

            // Only set price if we have a successful response with price_list data
            if (supplierPricingResponse.success && Object.keys(supplierPricingResponse.price_list).length > 0) {
              console.log(`Customer price list ID: ${customerPriceListId}`);
              console.log(`Available prices in supplier response:`, supplierPricingResponse.price_list);

              // Check if we have a valid customer price list ID and corresponding price
              if (customerPriceListId && supplierPricingResponse.price_list[customerPriceListId.toString()]) {
                const priceForCustomer = supplierPricingResponse.price_list[customerPriceListId.toString()];

                console.log(`Found price for customer price list ID ${customerPriceListId}: $${priceForCustomer}`);
                
                // Special logging for Alex Group
                if (selectedCustomer?.company_name === 'Alex Group' || selectedCustomer?.name === 'Alex Group') {
                  console.log(`ALEX GROUP PRICING: Setting price to $${priceForCustomer} for SKU ${value}`);
                  if (value === 'BS35-S' && Math.abs(priceForCustomer - 1343.04) < 0.01) {
                    console.log('SUCCESS: Alex Group is getting the correct price for BS35-S!');
                  }
                }

                // Update unit price with customer price list value - ONLY use API price, NO FALLBACK
                newItems[index].unitPrice = priceForCustomer;
                newItems[index].totalPrice = newItems[index].quantity.total * priceForCustomer; // Exclude GST

                console.log(`SKU ${value}: Using supplier price ${priceForCustomer} for price list ID ${customerPriceListId}`);

                // Prefer supplier description when available to ensure accuracy
                if (supplierPricingResponse.description) {
                  newItems[index].description = supplierPricingResponse.description;
                }
                
                // Store notes from supplier pricing response
                if (supplierPricingResponse.notes) {
                  newItems[index].notes = supplierPricingResponse.notes;
                }

                // Update state
                setFormData(prev => ({
                  ...prev,
                  items: newItems
                }));
                calculateTotals();
              } else {
                // No matching price found - do NOT use fallback, leave at 0
                console.warn(`SKU ${value}: No pricing found for customer price list ID ${customerPriceListId}`);
                console.warn(`Available price list keys:`, Object.keys(supplierPricingResponse.price_list));
                
                if (!selectedCustomer) {
                  console.warn(`No customer selected - cannot determine which price to use from price_list`);
                } else if (!customerPriceListId) {
                  console.warn(`Customer selected but no price_list_id set - cannot determine which price to use`);
                } else {
                  console.warn(`Customer price_list_id ${customerPriceListId} not found in available prices`);
                }
                
                // For Alex Group, this is a critical error
                if (selectedCustomer?.company_name === 'Alex Group' || selectedCustomer?.name === 'Alex Group') {
                  console.error('CRITICAL: Alex Group customer but no price found for their price list ID!');
                  console.error('Alex Group price list ID:', customerPriceListId);
                  console.error('Available prices:', supplierPricingResponse.price_list);
                }
                
                // DO NOT use fallback - leave price at 0 if no matching price found
                // Update state to reflect this (price remains 0)
                setFormData(prev => ({
                  ...prev,
                  items: newItems
                }));
                calculateTotals();
              }
            } else {
              // No supplier pricing found - do NOT use fallback, leave at 0
              console.warn(`SKU ${value}: No supplier pricing found in API response`);
              console.warn(`Response success: ${supplierPricingResponse.success}`);
              console.warn(`Price list keys:`, Object.keys(supplierPricingResponse.price_list || {}));
              
              // DO NOT use fallback - leave price at 0
              // Update state to reflect this (price remains 0)
              setFormData(prev => ({
                ...prev,
                items: newItems
              }));
              calculateTotals();
            }

          } catch (error) {
            console.error('Error fetching supplier pricing:', error);
                  console.error('DO NOT use fallback - only use correct price from API');

            // DO NOT use fallback on error - leave price at 0
            // Update state to reflect this (price remains 0)
            setFormData(prev => ({
              ...prev,
              items: newItems
            }));
            calculateTotals();
          }
        };

        // Execute the pricing fetch
        fetchSupplierPricing();

        // Also fetch from inventory API to ensure data is accurate and up-to-date (for description and stock updates)
        const fetchInventoryData = async () => {
          try {
            console.log(`Fetching individual inventory data for SKU: ${value}`);
            const inventory = await inventoryService.getInventoryBySku(value);
            if (inventory) {
              // Store individual SKU data for availability calculations
              setIndividualSkuData(prev => ({
                ...prev,
                [value]: inventory
              }));

              let shouldUpdate = false;
              
              // Update description if not already set
              if (!newItems[index].description) {
                newItems[index].description = inventory.description || inventory.style_code || '';
                shouldUpdate = true;
              }

              // Update carton constraints with fresh data
              const cartonConstraint = getCartonConstraints(inventory);
              newItems[index].cartonConstraint = cartonConstraint;
              setCartonConstraints(prev => ({
                ...prev,
                [value]: cartonConstraint
              }));
              shouldUpdate = true;

              // Update state if needed
              if (shouldUpdate) {
                setFormData(prev => ({
                  ...prev,
                  items: newItems
                }));
                calculateTotals();
              }

              console.log(`SKU ${value}: Stored inventory data with available_stock: ${inventory.available_stock}`);
              
              // Automatically update deductFromStock based on availability
              const totalQuantity = newItems[index].quantity?.total || 0;
              if (totalQuantity > 0) {
                // Use a small delay to ensure the state update has completed
                setTimeout(() => {
                  updateDeductFromStockBasedOnAvailability(index, value, totalQuantity);
                }, 100);
              }
            }
          } catch (error) {
            console.error('Error fetching inventory data:', error);
            // Store empty data to prevent repeated failed lookups
            setIndividualSkuData(prev => ({
              ...prev,
              [value]: null
            }));
          }
        };

        fetchInventoryData();
      } else {
        // If SKU is cleared, reset carton constraints and M2 rounding
        newItems[index].cartonConstraint = undefined;
        newItems[index].m2RoundingResult = undefined;
        setCartonConstraints(prev => {
          const newConstraints = { ...prev };
          delete newConstraints[newItems[index].sku];
          return newConstraints;
        });
      }
    } else if (field === 'description') {
      newItems[index].description = value;
    } else if (field === 'unitPrice') {
      const numValue = Number(value) || 0;
      if (numValue < 0) return;
      newItems[index].unitPrice = numValue;
    } else if (field === 'deductFromStock') {
      newItems[index].deductFromStock = Boolean(value);
    }

    // Update total price whenever quantity or unit price changes
    if (field === 'unitPrice' || field.startsWith('quantity.')) {
      newItems[index].totalPrice = newItems[index].quantity.total * newItems[index].unitPrice; // Exclude GST
      
      // Auto-update deductFromStock when quantity changes
      if (field.startsWith('quantity.') && newItems[index].sku) {
        const totalQuantity = newItems[index].quantity.total;
        if (totalQuantity > 0) {
          // Use a small delay to ensure the state update has completed
          setTimeout(() => {
            updateDeductFromStockBasedOnAvailability(index, newItems[index].sku, totalQuantity);
          }, 100);
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      items: newItems
    }));

    // Calculate totals will be called after all changes are made
    setTimeout(() => {
      const newTotals = calculateTotals(newItems);
      setFormData(prev => ({ ...prev, totals: newTotals }));
    }, 0);
  };

  // Add a new empty item to the invoice
  const handleAddItem = () => {
    // Create a unique ID for the new item
    const newId = Math.random().toString(36).substring(2, 11);

    setFormData(prev => {
      const newItem: InvoiceItem = {
        id: newId,
        sku: '',
        description: '',
        quantity: {
          units: null,
          boxes: null,
          pieces: null,
          m2: null,
          blm: null,
          total: 0
        },
        unitPrice: 0,
        totalPrice: 0,
        deductFromStock: false
      };

      const updatedItems = [...prev.items, newItem];

      return {
        ...prev,
        items: updatedItems
      };
    });
  };

  // Remove an item from the invoice
  const handleRemoveItem = (index: number) => {
    setFormData(prev => {
      const updatedItems = prev.items.filter((_, i) => i !== index);

      // Recalculate totals
      const totals = calculateTotals(updatedItems);

      return {
        ...prev,
        items: updatedItems,
        totals
      };
    });
  };

  // Calculate invoice totals
  const calculateTotals = (items = formData.items) => {
    const orderTotal = items.reduce((sum, item) => sum + (item?.totalPrice || 0), 0);
    
    // Safely access shipping and preserve existing discount value
    const shipping = formData?.totals?.shipping || 0;
    const discount = formData?.totals?.discount || 0; // Preserve existing discount percentage
    
    // Calculate discount amount - applies only to item total (orderTotal)
    const discountAmount = orderTotal * (discount / 100);
    
    // Calculate subtotal after discount
    const subtotalAfterDiscount = orderTotal - discountAmount;
    
    // Extract GST from subtotalAfterDiscount and shipping (10% of total excluding GST)
    const gst = (subtotalAfterDiscount + shipping) * 0.10;
    
    // Calculate credit card surcharge (1.5% of everything including GST) only if payment mode is Credit Card
    const amountBeforeSurcharge = subtotalAfterDiscount + shipping + gst;
    const surcharge = formData?.modeOfPayment === 'Credit Card' ? amountBeforeSurcharge * 0.015 : 0;
    
    // Calculate grand total = amount before surcharge + surcharge
    const grandTotal = amountBeforeSurcharge + surcharge;

    // Update the form data with the new totals, preserving the discount
    setFormData(prev => ({
      ...prev,
      totals: {
        orderTotal,
        surcharge,
        gst,
        discount, // Keep the existing discount percentage
        shipping,
        grandTotal
      }
    }));

    return {
      orderTotal,
      surcharge,
      gst,
      discount,
      shipping,
      grandTotal
    };
  };

  return (
    <>


      {/* Removed breadcrumb navigation as it's now in the page component */}

      <FormContainer onSubmit={handleSubmit}>
        <FormBoxesContainer>
          <FormItemBox>
            <FormBoxHeading data-required="*">Store Type</FormBoxHeading>
            <SelectWrapper>
              <FormSelect
                name="storeType"
                value={formData.storeType}
                onChange={handleInputChange}
                disabled={isLoadingStoreTypes || storeFilter.isStoreRestricted}
                style={{
                  backgroundColor: storeFilter.isStoreRestricted ? '#F9FAFB' : 'white',
                  cursor: storeFilter.isStoreRestricted ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">
                  {storeFilter.isStoreRestricted && storeFilter.userStoreType
                    ? storeFilter.userStoreType
                    : 'Select Store Type'
                  }
                </option>
                {storeTypes && storeTypes.map(type => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </FormSelect>
            </SelectWrapper>
            {storeFilter.isStoreRestricted && (
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Store type is auto-selected based on your role
              </div>
            )}
          </FormItemBox>

          <FormItemBox>
            <FormBoxHeading data-required="*">Sale Type</FormBoxHeading>
            <SelectWrapper>
              <FormSelect name="saleType" value={formData.saleType} onChange={handleInputChange}>
                <option value="">Select Sale Type</option>
                <option value="Trade">Trade</option>
                <option value="Retail">Retail</option>
                <option value="Store">Store</option>
              </FormSelect>
            </SelectWrapper>
          </FormItemBox>

          <FormItemBox>
            <FormBoxHeading data-required="*">Mode of Payment</FormBoxHeading>
            <SelectWrapper>
              <FormSelect name="modeOfPayment" value={formData.modeOfPayment} onChange={handleInputChange}>
                <option value="">Select Payment Mode</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </FormSelect>
            </SelectWrapper>
          </FormItemBox>

          <FormItemBox>
            <FormBoxHeading>Customer PO</FormBoxHeading>
            <FormInput
              type="text"
              name="poNumber"
              value={formData.poNumber}
              onChange={handleInputChange}
              disabled={isPONumberDisabled}
              placeholder={isPONumberDisabled ? "Not required for Store" : ""}
            />
          </FormItemBox>

          <FormItemBox>
            <FormBoxHeading data-required="*">Invoice To</FormBoxHeading>
            <CustomerSelectWrapper>
              <CustomerSelect
                name="invoiceTo"
                value={formData.invoiceTo}
                onChange={(customerId, customerName) => handleCustomerSelect(customerId, customerName)}
                required
                placeholder="Search Customer"
              />
            </CustomerSelectWrapper>
          </FormItemBox>

          <FormItemBox>
            <FormBoxHeading data-required="*">Deliver to Address</FormBoxHeading>
            <DeliverToAddressInput
              type="text"
              name="deliverToAddress"
              value={formData.deliverToAddress}
              onChange={handleInputChange}
              disabled={customerPickup}
              required
            />
          </FormItemBox>

          <FormItemBox>
            <FormBoxHeading>Invoice No</FormBoxHeading>
            <FormInput
              type="text"
              name="invoiceNo"
              value={formData.invoiceNo}
              onChange={handleInputChange}
              placeholder="Auto-generated"
              disabled={true}
            />
          </FormItemBox>
        </FormBoxesContainer>

        <BottomRowContainer>
          <FormItemBox className="notes-field">
            <FormBoxHeading>Notes</FormBoxHeading>
            <NotesTextarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
            />
          </FormItemBox>

          <FormItemBox className="quote-field">
            <FormBoxHeading>
              Linked to Quote
            </FormBoxHeading>
            <QuoteSelectWrapper ref={quoteDropdownRef}>
              {quoteSearchTerm && formData.linkedQuote ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  height: '36px',
                  padding: '0.375rem 0.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '0.25rem',
                  backgroundColor: '#F9FAFB'
                }}>
                  <span style={{ fontWeight: '500' }}>{quoteSearchTerm}</span>
                  <button 
                    type="button"
                    onClick={() => {
                      setQuoteSearchTerm('');
                      setFormData(prev => ({ ...prev, linkedQuote: '' }));
                    }}
                    style={{
                      marginLeft: 'auto',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6B7280',
                      padding: '2px 5px'
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <QuoteSearchInput
                  type="text"
                  value={quoteSearchTerm}
                  onChange={(e) => {
                    setQuoteSearchTerm(e.target.value);
                    setIsQuoteDropdownOpen(true);
                  }}
                  onClick={() => setIsQuoteDropdownOpen(true)}
                  placeholder={
                    filterQuotesByCustomer 
                      ? "Search quotes for selected customer" 
                      : "Search all quotes"
                  }
                  disabled={isLoadingQuotes}
                />
              )}
              <QuoteDropdown isOpen={isQuoteDropdownOpen && !formData.linkedQuote}>
                {filteredQuotes.length > 0 ? (
                  filteredQuotes.map(quote => (
                    <QuoteOption
                      key={quote.id}
                      onClick={() => handleQuoteSelect(quote)}
                    >
                      <div style={{ fontWeight: '500' }}>{quote.quote_no}</div>
                      {!filterQuotesByCustomer && quote.customer_name && (
                        <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '2px' }}>
                          Customer: {quote.customer_name}
                        </div>
                      )}
                    </QuoteOption>
                  ))
                ) : (
                  <QuoteOption style={{ cursor: 'default' }}>
                    <div style={{ color: '#6B7280' }}>
                      {filterQuotesByCustomer 
                        ? "No quotes found for selected customer" 
                        : "No quotes match your search"}
                    </div>
                  </QuoteOption>
                )}
              </QuoteDropdown>
            </QuoteSelectWrapper>
          </FormItemBox>

          <FormItemBox className="date-field">
            <FormBoxHeading data-required="*">Date</FormBoxHeading>
            <DateInputWrapper>
              <StyledDateInput
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
              />
            </DateInputWrapper>
          </FormItemBox>

          <FormItemBox className="checkbox-field">
            <CheckboxContainer>
              <CheckboxInput
                type="checkbox"
                id="dontSendPo"
                name="dontSendPo"
                checked={formData.dontSendPo}
                onChange={handleCheckboxChange}
              />
              <CheckboxLabel htmlFor="dontSendPo">Don't Send PO</CheckboxLabel>
            </CheckboxContainer>
            <CustomerPickupContainer>
              <CustomerPickupInput
                type="checkbox"
                id="customerPickup"
                checked={customerPickup}
                onChange={handleCustomerPickupChange}
              />
              <CustomerPickupLabel htmlFor="customerPickup">
                Customer Pickup
              </CustomerPickupLabel>
            </CustomerPickupContainer>
          </FormItemBox>
        </BottomRowContainer>



        <TableWrapper>
        <Table>
          <TableHeader>
            <tr>
              <th style={{ width: '60px', minWidth: '60px' }} rowSpan={2}>Notes</th>
              <th style={{ width: 'auto', minWidth: '250px' }} rowSpan={2}>BWA SKU</th>
              <th style={{ width: 'auto', minWidth: '250px' }} rowSpan={2}>Description</th>
              <th colSpan={6} style={{ textAlign: 'center' }}>Quantity</th>
              <th style={{ width: '110px', minWidth: '110px', textAlign: 'right' }} rowSpan={2}>Unit Price<br />excl GST</th>
              <th style={{ width: '120px', minWidth: '120px', textAlign: 'right' }} rowSpan={2}>Total Price<br />(excluding GST)</th>
              <th style={{ width: '65px', minWidth: '65px', textAlign: 'right' }} rowSpan={2}>GST</th>
              <th style={{ width: '120px', minWidth: '120px', textAlign: 'right' }} rowSpan={2}>Total Price<br />(including GST)</th>
              <th style={{ width: '90px', minWidth: '90px', textAlign: 'center' }} rowSpan={2}>Availability</th>
              <th style={{ width: '100px', minWidth: '100px', textAlign: 'center' }} rowSpan={2}>Deduct from<br />Available Stock</th>
              <th style={{ width: '50px', minWidth: '50px' }} rowSpan={2}></th>
            </tr>
            <tr>
              <th style={{ width: '65px', minWidth: '65px', padding: '0.5rem' }}>Units</th>
              <th style={{ width: '65px', minWidth: '65px', padding: '0.5rem' }}>Boxes</th>
              <th style={{ width: '65px', minWidth: '65px', padding: '0.5rem' }}>Pieces</th>
              <th style={{ width: '65px', minWidth: '65px', padding: '0.5rem' }}>M2</th>
              <th style={{ width: '65px', minWidth: '65px', padding: '0.5rem' }}>BLM</th>
              <th style={{ width: '70px', minWidth: '70px', padding: '0.5rem' }}>Total</th>
            </tr>
          </TableHeader>
          <TableBody>
            {formData.items.length === 0 ? (
              <tr>
                <td colSpan={16} style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '20px' }}>
                    <div style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '4px' }}>Add item</div>
                    <AddItemButton 
                      type="button" 
                      onClick={handleAddItem}
                      disabled={!formData.saleType || formData.saleType.trim() === '' ||
                               !formData.modeOfPayment || formData.modeOfPayment.trim() === '' ||
                               !formData.invoiceTo || formData.invoiceTo.trim() === '' ||
                               !formData.deliverToAddress || formData.deliverToAddress.trim() === '' ||
                               !formData.date || formData.date.trim() === ''}
                      title={(!formData.saleType || formData.saleType.trim() === '' ||
                              !formData.modeOfPayment || formData.modeOfPayment.trim() === '' ||
                              !formData.invoiceTo || formData.invoiceTo.trim() === '' ||
                              !formData.deliverToAddress || formData.deliverToAddress.trim() === '' ||
                              !formData.date || formData.date.trim() === '') ? 'Please fill in all required fields (*) first' : 'Add item'}
                    >+</AddItemButton>
                  </div>
                </td>
              </tr>
            ) : (
              formData.items.map((item, index) => {
                const activeQuantityField = getActiveQuantityField(item.quantity, item);
                
                // Check if required fields are filled
                const isBaseFieldsDisabled = !formData.saleType || formData.saleType.trim() === '' ||
                                      !formData.modeOfPayment || formData.modeOfPayment.trim() === '' ||
                                      !formData.invoiceTo || formData.invoiceTo.trim() === '' ||
                                      !formData.deliverToAddress || formData.deliverToAddress.trim() === '' ||
                                      !formData.date || formData.date.trim() === '';
                const isFieldDisabled = isBaseFieldsDisabled || !item.sku;
                
                // Calculate availability for this item
                const inventoryItem = getInventoryItemData(item.sku);
                const availableStock = inventoryItem?.available_stock || 0;
                const totalQuantity = item.quantity.total || 0;
                
                let availabilityDisplay = null;
                if (item.sku && totalQuantity > 0) {
                  // Debug log for availability calculation
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`Availability check for SKU ${item.sku}:`, {
                      inventoryItem: !!inventoryItem,
                      availableStock,
                      totalQuantity,
                      source: individualSkuData[item.sku] ? 'individual' : 'main_inventory'
                    });
                  }

                  if (totalQuantity <= availableStock) {
                    availabilityDisplay = (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        gap: '2px',
                        fontSize: '0.85rem',
                        lineHeight: '1.1'
                      }}>
                        <span style={{ color: '#22c55e', fontWeight: 'bold' }}>
                          Available
                        </span>
                        {availableStock > 0 && (
                          <span style={{ color: '#6B7280', fontSize: '0.75rem' }}>
                            {availableStock} units
                          </span>
                        )}
                      </div>
                    );
                  } else {
                    const required = totalQuantity - availableStock;
                    availabilityDisplay = (
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        gap: '2px',
                        fontSize: '0.85rem',
                        lineHeight: '1.1'
                      }}>
                        <span style={{ color: '#22c55e', fontWeight: 'bold' }}>
                          Available {availableStock}
                        </span>
                        <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
                          Required {required}
                        </span>
                      </div>
                    );
                  }
                }
                
                return (
                  <tr key={item.id}>
                    <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                      <NotesIcon
                        hasNotes={!!item.notes && item.notes.length > 0}
                        onClick={() => {
                          if (!isBaseFieldsDisabled && item.sku && item.sku.trim().length > 0) {
                            setCurrentNotesItemId(item.id);
                            setCurrentNotesValue(item.notes || '');
                            setShowNotesModal(true);
                          }
                        }}
                        style={{ 
                          cursor: (!isBaseFieldsDisabled && item.sku && item.sku.trim().length > 0) ? 'pointer' : 'not-allowed',
                          opacity: (!isBaseFieldsDisabled && item.sku && item.sku.trim().length > 0) ? 1 : 0.5
                        }}
                      >
                        <img src={invoicesNotesIcon} alt="Notes" />
                      </NotesIcon>
                    </td>
                    <td className="wrap">
                      <SKUSelectWrapper className="dropdown-container">
                        {item.sku && getInventoryItemData(item.sku) ? (
                          <SelectedSKUContainer>
                            <SkuImage
                              imagePath={getInventoryItemData(item.sku)?.image_path}
                              alt="SKU"
                              width={32}
                              height={32}
                              enableModal={true}
                              showBlankWhenNoImage={true}
                              key={`${item.sku}-${lastInventoryRefresh}`}
                            />
                            <SKUSearchInput
                              ref={(el: HTMLTextAreaElement | null) => { skuInputRefs.current[index] = el; }}
                              value={skuSearchTerms[index] || ''}
                              onChange={(e) => {
                                if (isBaseFieldsDisabled) return;
                                const newValue = e.target.value;
                                setSKUSearchTerms(prev => ({
                                  ...prev,
                                  [index]: newValue
                                }));
                                setOpenSKUDropdowns(prev => ({
                                  ...prev,
                                  [index]: true
                                }));
                                updateDropdownPosition(index);
                                // If the search term is cleared, also clear the item SKU
                                if (!newValue.trim()) {
                                  handleItemChange(index, 'sku', '');
                                }
                              }}
                              onFocus={() => {
                                if (isBaseFieldsDisabled) {
                                  setOpenSKUDropdowns(prev => ({
                                    ...prev,
                                    [index]: false
                                  }));
                                  return;
                                }
                                setOpenSKUDropdowns(prev => ({
                                  ...prev,
                                  [index]: true
                                }));
                                updateDropdownPosition(index);
                                // Refresh inventory items if needed
                                if (inventoryItems.length === 0) {
                                  fetchInventoryItems();
                                }
                              }}
                              onBlur={() => handleSKUBlur(index)}
                              placeholder="Search SKU"
                              disabled={isBaseFieldsDisabled}
                              style={{ flex: 1, backgroundColor: isBaseFieldsDisabled ? '#F9FAFB' : 'white', cursor: isBaseFieldsDisabled ? 'not-allowed' : 'text' }}
                            />
                          </SelectedSKUContainer>
                        ) : (
                          <SKUSearchInput
                            ref={(el: HTMLTextAreaElement | null) => { skuInputRefs.current[index] = el; }}
                            value={skuSearchTerms[index] || ''}
                            onChange={(e) => {
                              if (isBaseFieldsDisabled) return;
                              const newValue = e.target.value;
                              setSKUSearchTerms(prev => ({
                                ...prev,
                                [index]: newValue
                              }));
                              setOpenSKUDropdowns(prev => ({
                                ...prev,
                                [index]: true
                              }));
                              updateDropdownPosition(index);
                              // If the search term is cleared, also clear the item SKU
                              if (!newValue.trim()) {
                                handleItemChange(index, 'sku', '');
                              }
                            }}
                            onFocus={() => {
                              if (isBaseFieldsDisabled) {
                                setOpenSKUDropdowns(prev => ({
                                  ...prev,
                                  [index]: false
                                }));
                                return;
                              }
                              setOpenSKUDropdowns(prev => ({
                                ...prev,
                                [index]: true
                              }));
                              updateDropdownPosition(index);
                              // Refresh inventory items if needed
                              if (inventoryItems.length === 0) {
                                fetchInventoryItems();
                              }
                            }}
                            onBlur={() => handleSKUBlur(index)}
                            placeholder="Search SKU"
                            disabled={isBaseFieldsDisabled}
                            style={{ backgroundColor: isBaseFieldsDisabled ? '#F9FAFB' : 'white', cursor: isBaseFieldsDisabled ? 'not-allowed' : 'text' }}
                          />
                        )}
                        {skuDropdownPositions[index] && !isBaseFieldsDisabled && createPortal(
                          <SKUDropdown 
                            data-sku-dropdown="true"
                            isOpen={openSKUDropdowns[index]}
                            top={skuDropdownPositions[index].top}
                            left={skuDropdownPositions[index].left}
                            width={skuDropdownPositions[index].width}
                          >
                            {isLoadingInventory ? (
                              <SKUOption>
                                <div style={{ padding: '8px', textAlign: 'center', color: '#6B7280' }}>
                                  Loading SKUs...
                                </div>
                              </SKUOption>
                            ) : filteredInventoryItemsByRow[index]?.length > 0 ? (
                              filteredInventoryItemsByRow[index].map(invItem => (
                                <SKUOption
                                  key={invItem.id}
                                  onClick={() => {
                                    if (!isBaseFieldsDisabled) {
                                      handleSKUSelect(index, invItem);
                                    }
                                  }}
                                  style={{ 
                                    cursor: isBaseFieldsDisabled ? 'not-allowed' : 'pointer',
                                    opacity: isBaseFieldsDisabled ? 0.5 : 1
                                  }}
                                >
                                  <SkuImage
                                    imagePath={invItem.image_path}
                                    alt="SKU"
                                    width={40}
                                    height={40}
                                    enableModal={true}
                                    showBlankWhenNoImage={true}
                                    key={`${invItem.id}-${lastInventoryRefresh}`}
                                  />
                                  <SKUOptionContent>
                                    <div style={{ fontWeight: '500' }}>{invItem.sku_code}</div>
                                    {invItem.description && (
                                      <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '2px' }}>
                                        {invItem.description}
                                      </div>
                                    )}
                                  </SKUOptionContent>
                                </SKUOption>
                              ))
                            ) : skuSearchTerms[index]?.trim() ? (
                              <SKUOption>
                                <div style={{ padding: '8px', textAlign: 'center', color: '#6B7280' }}>
                                  No SKUs found matching "{skuSearchTerms[index]}"
                                </div>
                              </SKUOption>
                            ) : (
                              <SKUOption>
                                <div style={{ padding: '8px', textAlign: 'center', color: '#6B7280' }}>
                                  Type to search for SKUs
                                </div>
                              </SKUOption>
                            )}
                          </SKUDropdown>,
                          document.body
                        )}
                      </SKUSelectWrapper>
                    </td>
                    <td className="wrap">
                      <FormTextarea
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        style={{ width: '100%', minHeight: '48px' }}
                        rows={2}
                        disabled={isFieldDisabled}
                      />
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.units || ''}
                          onChange={(e) => handleItemChange(index, 'quantity.units', e.target.value)}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'units') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'units') || isFieldDisabled}
                        />
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.boxes || ''}
                          onChange={(e) => handleItemChange(index, 'quantity.boxes', e.target.value)}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'boxes') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'boxes') || isFieldDisabled}
                        />
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.pieces || ''}
                          onChange={(e) => handleItemChange(index, 'quantity.pieces', e.target.value)}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'pieces') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'pieces') || isFieldDisabled}
                        />
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.m2 || ''}
                          onChange={(e) => handleItemChange(index, 'quantity.m2', e.target.value)}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'm2') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'm2') || isFieldDisabled}
                          readOnly={activeQuantityField === 'blm' || isFieldDisabled}
                          style={activeQuantityField === 'blm' || isFieldDisabled ? { backgroundColor: '#f5f5f5', color: '#666' } : {}}
                        />
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <QuantityInput
                          type="number"
                          value={item.quantity.blm || ''}
                          onChange={(e) => {
                            // Immediately trigger full BLM logic as user types
                            handleItemChange(index, 'quantity.blm', e.target.value);
                          }}
                          isDisabled={(activeQuantityField !== null && activeQuantityField !== 'blm') || isFieldDisabled}
                          disabled={(activeQuantityField !== null && activeQuantityField !== 'blm') || isFieldDisabled}
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                        />
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <QuantityInput
                          type="text"
                          value={item.quantity.total.toFixed(2)}
                          readOnly
                          style={{ marginRight: '4px' }}
                        />
                        {(item.cartonConstraint?.hasConstraints || item.m2RoundingResult?.isRounded) && (
                          <SmallInfoIcon 
                            showTooltip={cartonInfoTooltip.visible && cartonInfoTooltip.itemIndex === index}
                            onMouseEnter={() => handleCartonInfoHover(index, true)}
                            onMouseLeave={() => handleCartonInfoHover(index, false)}
                            title="Carton Information"
                          >
                            i
                            <CartonInfoTooltip show={cartonInfoTooltip.visible && cartonInfoTooltip.itemIndex === index}>
                              {item.m2RoundingResult?.isRounded 
                                ? item.m2RoundingResult.message 
                                : item.cartonConstraint?.hasConstraints 
                                  ? formatCartonInfo(item.cartonConstraint)
                                  : 'No carton information available'
                              }
                            </CartonInfoTooltip>
                          </SmallInfoIcon>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <PriceInput
                          type="number"
                          value={item.unitPrice}
                          readOnly={!canEditPrice}
                          step="0.01"
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          style={canEditPrice ? { 
                            width: '110px',
                            height: '36px'
                          } : { 
                            backgroundColor: '#f9fafb',
                            cursor: 'not-allowed',
                            color: '#6B7280',
                            width: '110px',
                            height: '36px'
                          }}
                        />
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                        <PriceInput
                          type="text"
                          value={(item.quantity.total * item.unitPrice).toFixed(2)}
                          readOnly
                          style={{ 
                            backgroundColor: '#f9fafb',
                            cursor: 'not-allowed',
                            color: '#6B7280',
                            width: '100%',
                            maxWidth: '110px',
                            height: '36px'
                          }}
                        />
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <PriceInput
                          type="text"
                          value={(item.quantity.total * item.unitPrice * 0.1).toFixed(2)}
                          readOnly
                          style={{ 
                            backgroundColor: '#f9fafb',
                            cursor: 'not-allowed',
                            color: '#6B7280',
                            width: '65px',
                            height: '36px'
                          }}
                        />
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <PriceInput
                          type="text"
                          value={(item.quantity.total * item.unitPrice * 1.1).toFixed(2)}
                          readOnly
                          style={{
                            width: '120px',
                            height: '36px'
                          }}
                        />
                      </div>
                    </td>
                    <td>{availabilityDisplay}</td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={item.deductFromStock}
                        onChange={(e) => handleItemChange(index, 'deductFromStock', e.target.checked)}
                        style={{ 
                          transform: 'scale(1.1)',
                          margin: '0',
                          cursor: 'pointer'
                        }}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <AddItemButton 
                          type="button" 
                          onClick={handleAddItem}
                          disabled={isFieldDisabled}
                          title={isFieldDisabled ? 'Please fill in all required fields (*) first' : 'Add item'}
                        >+</AddItemButton>
                        {formData.items.length > 1 && (
                          <RemoveItemButton type="button" onClick={() => handleRemoveItem(index)}>×</RemoveItemButton>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </TableBody>
        </Table>
        </TableWrapper>

        <TotalSection>
          <TotalRow>
            <TotalLabel>Total Order:</TotalLabel>
            <CurrencySymbol>$</CurrencySymbol>
            <TotalInput
              type="text"
              value={formData.totals.orderTotal.toFixed(2)}
              readOnly
            />
          </TotalRow>
          <TotalRow>
            <TotalLabel>Discount (%):</TotalLabel>
            <TotalInput
              type="number"
              value={formData.totals.discount}
              onChange={handleDiscountChange}
              step="0.01"
              min="0"
              max="100"
              placeholder="0"
            />
          </TotalRow>
          <TotalRow>
            <TotalLabel>
              <ClickableInfoIcon onClick={handleShippingInfoClick}>
                i
              </ClickableInfoIcon>
              Shipping:
            </TotalLabel>
            <CurrencySymbol>$</CurrencySymbol>
            <TotalInput
              type="number"
              value={shippingInputValue}
              onChange={handleShippingChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              style={{
                color: shippingInputValue === '' ? '#9CA3AF' : '#111827'
              }}
              onBlur={handleShippingBlur}
            />
          </TotalRow>
          <TotalRow>
            <TotalLabel>Total GST:</TotalLabel>
            <CurrencySymbol>$</CurrencySymbol>
            <TotalInput
              type="text"
              value={formData.totals.gst.toFixed(2)}
              readOnly
            />
          </TotalRow>
          <TotalRow>
            <TotalLabel>Credit Card Surcharge (1.5%):</TotalLabel>
            <CurrencySymbol>$</CurrencySymbol>
            <TotalInput
              type="text"
              value={formData.totals.surcharge.toFixed(2)}
              readOnly
              style={{
                backgroundColor: formData.modeOfPayment !== 'Credit Card' ? '#F9FAFB' : 'white',
                color: formData.modeOfPayment !== 'Credit Card' ? '#6B7280' : '#111827',
                cursor: 'not-allowed'
              }}
            />
          </TotalRow>
          <Divider />
          <TotalRow>
            <GrandTotalLabel>Grand Total:</GrandTotalLabel>
            <CurrencySymbol>$</CurrencySymbol>
            <TotalInput
              type="text"
              value={formData.totals.grandTotal.toFixed(2)}
              readOnly
              style={{ fontWeight: 600, backgroundColor: 'white' }}
            />
          </TotalRow>
        </TotalSection>



        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: '1rem' }}>
          <Button type="button" secondary onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" secondary onClick={handleSaveAsDraftInternal} disabled={isSubmitting || parentIsLoading}>
            Save as Draft
          </Button>
          <Button type="submit" primary disabled={saveButtonDisabled}>
            {isSubmitting || parentIsLoading ? 'Saving' : 'Save Invoice'}
          </Button>
        </div>
      </FormContainer>

      {/* Shipping Details Modal */}
      {isShippingModalOpen && (
        <ModalOverlay onClick={handleCloseShippingModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>BWA DELIVERY PRICELIST</ModalTitle>
              <ModalCloseButton onClick={handleCloseShippingModal}>
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <h2 style={{ 
                  color: '#DC2626', 
                  fontWeight: 'bold', 
                  fontSize: '16px', 
                  margin: '0 0 20px 0',
                  textTransform: 'uppercase'
                }}>
                  SUPPLIER TILE DELIVERIES TO BWA – PASS ON TO CUSTOMERS
                </h2>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>1 – 3 BOXES</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$30.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>4 – 7 BOXES</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$40.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>8 – 12 BOXES</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$50.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>SMALL SKID (13 – 19 BOXES)</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$60.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>PALLET (20 - 40 BOXES)</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$90.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>LARGE PALLET (40 – 60 BOXES)</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$100.00</span>
                </div>
              </div>

              <div style={{ 
                textAlign: 'center', 
                color: '#16A34A', 
                fontSize: '12px', 
                fontWeight: 'bold',
                margin: '15px 0',
                textTransform: 'uppercase'
              }}>
                FOR SMALLER BOX ORDERS EG 75*300MM CHARGE LESS THAN ABOVE. EG: 24 BOXES 75*300MM = $60.00
              </div>

              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <h2 style={{ 
                  color: '#DC2626', 
                  fontWeight: 'bold', 
                  fontSize: '16px', 
                  margin: '0',
                  textTransform: 'uppercase'
                }}>
                  DELIVERY FEES BWA TO CUSTOMER (TILES, BATHROOM WARE & TAPWARE)
                </h2>
                <div style={{ 
                  color: '#DC2626', 
                  fontWeight: 'bold', 
                  fontSize: '14px', 
                  margin: '5px 0 15px 0',
                  textTransform: 'uppercase'
                }}>
                  (BWA DELIVERY TO CUSTOMER)
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>UNDER $250.00 (EX GST)</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$25.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>SMALL LOAD</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$70.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>LARGE LOAD</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$140.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>BWA HALLAM TILES (PER PALLET) FORKLIFT DELIVERY</strong></span>
                  <span style={{ fontWeight: 'bold' }}>$100.00 (HALLAM CUSTOMERS ONLY)</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dotted #666' }}>
                  <span><strong>BWA SALE TILES (PER PALLET) FORKLIFT DELIVERY</strong></span>
                  <span style={{ fontWeight: 'bold' }}>OBTAIN QUOTE FROM FREIGHT CO.</span>
                </div>
              </div>

              <div style={{ 
                textAlign: 'center', 
                color: '#16A34A', 
                fontSize: '12px', 
                fontWeight: 'bold',
                margin: '15px 0',
                textTransform: 'uppercase'
              }}>
                FOR BWA SALE FORKLIFT DELIVERIES – CHECK WITH YOUR FREIGHT COMPANY & PASS ON PRICE PER PALLET
              </div>

              <div style={{ 
                textAlign: 'center', 
                fontSize: '12px', 
                fontStyle: 'italic',
                margin: '20px 0 0 0'
              }}>
                (Note: PRICES DO NOT INCLUDE GST)
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Notes Modal */}
      {showNotesModal && (
        <NotesModalOverlay onClick={() => {
          setShowNotesModal(false);
          setCurrentNotesItemId(null);
          setCurrentNotesValue('');
        }}>
          <NotesModalContent onClick={(e) => e.stopPropagation()}>
            <NotesModalTitle>Product Notes</NotesModalTitle>
            <NotesModalTextarea
              value={currentNotesValue}
              readOnly
              placeholder="No notes available for this product"
            />
            <NotesButtonContainer>
              <CloseNotesButton onClick={() => {
                setShowNotesModal(false);
                setCurrentNotesItemId(null);
                setCurrentNotesValue('');
              }}>
                Close
              </CloseNotesButton>
            </NotesButtonContainer>
          </NotesModalContent>
        </NotesModalOverlay>
      )}

      {/* Exit Confirmation Dialog */}
      <ExitConfirmationDialog
        isOpen={showExitConfirmation}
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />

      {/* Shipping Confirmation Dialog */}
      <ShippingConfirmationDialog
        isOpen={showShippingConfirmation}
        onConfirm={handleShippingConfirm}
        onCancel={handleShippingCancel}
      />
    </>
  );
};

export default EditInvoiceForm; 